import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, from, map, switchMap, tap, forkJoin, of, catchError, shareReplay, throwError, timeout, share } from 'rxjs';
import { PssAccount } from '../models/pss-account.model';
import { Optional } from '@angular/core';
import { SpriteCacheService } from './sprite-cache.service';
import {
  pssTimestamp,
  deviceLoginChecksum,
  emailPasswordChecksum,
  buildDeviceLoginPath,
  buildDeviceLoginBody,
} from './pss-checksum';

export interface PssSession {
  accessToken: string;
  userId: string;
  name: string;
  shipLevel: string;
  deviceKey: string;
  refreshToken: string;
  productionServer: string;
  loginXml: string;
  shipXml?: string;
  userDataXml?: string;
  email?: string;
  isAdmin?: boolean;
  isServiceAccount?: boolean;
  hasDeviceKey?: boolean;
  hasRefreshToken?: boolean;
  frame?: string;
  pin?: string;
  ribbon?: string;
}

export interface PssCrewMember {
  characterId: string;
  designId: string;
  customName: string;    // player-assigned name; empty if not renamed
  deployed: boolean;     // false = stash or in a lift room
  donated: boolean;      // true = currently donated to the alliance
  deploymentDate: string; // ISO date when donated; empty if not donated
  roomId: string;        // room instance ID ('0' if stash)
  stamina?: string;      // optional
  fatigue?: string;      // optional
  walk?: string;
  run?: string;
  roomDesignId: string;  // empty for stash crew (RoomId=0)
  level: string;
  xp: string;
  hpImp: string;
  pilotImp: string;
  attackImp: string;
  weaponImp: string;
  scienceImp: string;
  engineImp: string;
  abilityImp: string;
  repairImp: string;
  equipDesignIds: string[];  // resolved item design IDs for equipped gear
  equipItemIds?: string[]; // item instance IDs for equipped gear
  maxDonate?: number;
  isNew?: boolean;       // true only when parsed from an ActivateItem3 response with IsNew="true" — proves this is a freshly drawn recruit, required before auto-firing
}

export interface PssCharacterDesign {
  id: string;
  name: string;
  rarity: string;
  profileSpriteId: string;
  finalHp: string;
  finalPilot: string;
  finalAttack: string;
  finalWeapon: string;
  finalScience: string;
  finalEngine: string;
  finalAbility: string;
  finalRepair: string;
  maxLevel: string;
  specialAbilityType: string;
  specialAbilityArg: string;
}

export interface PssRoomDesign {
  id: string;
  name: string;
  rows: string;
  cols: string;
  roomType: string;
  imageSpriteId: string;
  flags: number;
  constructionTime: number;
  reloadTime: string;
  minDamage: string;
  maxDamage: string;
  capacity: string;
  enhancementType: string;   // e.g. "Weapon", "Engine", "Science", "Pilot"
  missileDesignId: string;   // non-"0" means room fires a single hard-wired missile
  itemRank: string;          // selects the ammo pool — matches PssAmmoDesign.rank
  manufactureType: string;   // "Missile" | "Craft" | "Android" | "Capacity" | "None"
}

export interface PssAmmoDesign {
  id: string;
  name: string;
  itemType: string;          // "Missile" | "Craft" | "Android" — matches PssRoomDesign.manufactureType
  reloadModifier: string;    // percentage — maps directly to ammo_mod in fire-rate formula
  missileDesignId: string;
  rank: string;              // matches PssRoomDesign.itemRank — which rooms can load it
  rootItemDesignId: string;  // Lv2-6 variants share their base ammo's root id
}

export interface PssRoom {
  roomId: string;
  roomDesignId: string;
  row: number;
  col: number;
  status: string;
  constructionStartDate?: string;
  manufactureStartDate?: string;
  upgradeRoomDesignId?: string;
}

export interface PssBoostGaugeInfo {
  nextAvailableDate: string;
  isReady: boolean;
  secondsUntilReady: number;
  boostAmountSeconds: number;
  hasBoostAmount: boolean;
  boostAmountHhmm: string;
  lastBoostDate: string;
  source: string;
}

export interface PssResearchDesign {
  id: string;
  name: string;
  level: string;
  logoSpriteId: string;
  researchTime: number;
}

export interface PssRushTarget {
  targetType: 'room' | 'ship' | 'research';
  targetId: string;
  label: string;
  status: string;
  endDate: string;
  startDate?: string;
  designId?: string;
  level?: string;
  logoSpriteId?: string;
  remainingSeconds: number | null;
}

export interface PssRoomAction {
  roomActionId: string;
  roomId: string;
  index: number;
  conditionTypeId: string;
  actionTypeId: string;
}

export interface PssStarSystem {
  id: string;
  title: string;
  x: string;
  y: string;
  links: string[];  // connected star system IDs
}

export interface PssStarSystemDetail {
  starSystemId: string;
  ownerType: string;       // 'None' | 'Alliance' | 'Player'
  ownerName: string;
  ownerId: string;
  engagementCooldownEndDate: string;  // ISO datetime
  immunityEndDate: string;            // ISO datetime
  attackerType: string;               // 'None' | 'Alliance' | 'Player'
  attackerName: string;
  attackerId: string;
}

export interface PssItemDesign {
  id: string;
  name: string;
  description: string;
  itemType: string;       // e.g. "Equipment", "Missile", "Instant", "Room", "Skin", etc.
  subtype: string;        // e.g. "EquipmentBody", "EquipmentPet", etc.
  logoSpriteId: string;
  rarity: string;         // Common, Elite, Epic, Hero, Special, Unique
  enhancementType: string; // HP, Pilot, Attack, Weapon, Science, Engine, Ability, Repair
  modifier: string;        // stat modifier value
  enhancementValue?: string; // alternate stat modifier value field returned by some endpoints
  enhancementType2?: string; // optional secondary stat type for multi-stat gear
  modifier2?: string;        // optional secondary stat modifier value
  enhancementValue2?: string; // optional secondary alternate modifier field
  bonusEnhancementType?: string; // per-instance bonus type (returned by item instance payload)
  bonusEnhancementValue?: string; // per-instance bonus value
  bonusEnhancementType2?: string;
  bonusEnhancementValue2?: string;
  flags: number;           // ItemDesignFlagType bitmask: 64=Consumable, 1024=UseOnCrew, 65536=UseOnRoom
}

export interface PssInventoryItem {
  designId: string;
  quantity: number;       // total count in inventory (including worn copies)
  equippedBy: PssCrewMember[];  // crew members currently wearing this item design
  equippedItemIds?: string[];  // item instance IDs currently equipped
  freeInstances: { id: string; qty: number }[];  // unequipped item instance IDs and their stack sizes
  instanceBonuses?: Array<{ itemId: string; enhancementType?: string; enhancementValue?: string; enhancementType2?: string; enhancementValue2?: string }>;
}

export interface PssShipDesign {
  id: string;
  name: string;
  rows: string;
  cols: string;
  interiorSpriteId: string;
  logoSpriteId: string;
  upgradeTime: number;
}

export interface PssConditionType {
  id: string;
  name: string;
  roomType: string;  // empty = universal; otherwise restricted to that RoomType
}

/** ItemTypes a room can manufacture and that the AI's "Set Item {N}" actions index into. */
const ROOM_BUILDABLE_ITEM_TYPES = new Set(['Missile', 'Craft', 'Android']);

export interface PssActionType {
  id: string;
  name: string;
  key: string;       // stable ActionTypeKey, e.g. "SetItem3" — safe to match on, unlike the localised name
  roomType: string;  // empty = universal; otherwise restricted to that RoomType
}

export interface PssCharacterAction {
  characterActionId: string;
  characterId: string;
  index: number;
  conditionTypeId: string;
  actionTypeId: string;
}

export interface PssFleetMessage {
  messageId: string;
  userId: string;
  userName: string;
  message: string;
  dateTime: string;
  frame?: string;
  pin?: string;
  ribbon?: string;
}

export interface PssPrivateMessage {
  messageId: string;
  fromUserId: string;   // the friend's userId (used as grouping key)
  fromName: string;     // sender display name for the individual message
  message: string;
  sentDate: string;
  sentByMe: boolean;    // true = logged-in user sent this message
  activityType?: string;
  isInvite?: boolean;
  frame?: string;
  pin?: string;
  ribbon?: string;
}

export interface PssFriend {
  userId: string;
  name: string;
  trophies: string;
  unreadMessages: number;
  lastInteraction: string;
  frame?: string;
  pin?: string;
  ribbon?: string;
}

export interface PssFleetMember {
  userId: string;
  name: string;
  trophies: string;
  highestTrophy: string;
  allianceScore: string;           // tournament stars
  tournamentBonusScore: string;    // tournament attack attempts
  allianceMembership: string;
  crewDonated: string;
  crewReceived: string;
  lastLoginDate: string;
}

export interface PssFleetInfo {
  allianceId: string;
  allianceName: string;
  trophy: string;
  divisionDesignId: string; // '0'=none, '1'=A, '2'=B, '3'=C, '4'=D
  ranking: string;          // tournament ranking within division ('0' = unknown)
  memberCount: string;
  members: PssFleetMember[];
}

export interface FleetDailySnapshot {
  id: number;
  allianceId: string;
  allianceName: string;
  userId: string;
  userName: string;
  snapshotDate: string;       // YYYY-MM-DD UTC
  tournamentStars: number;    // AllianceScore — stars this tournament period
  dailyAttacksDone: number;   // TournamentBonusScore — attacks today (resets UTC midnight)
  trophies: number;
  highestTrophy: number;
  crewDonated: number;
  crewReceived: number;
  membershipRank: string;
  lastLoginDate: string;
  recordedAt: string;
}

// ── Tournament interfaces ────────────────────────────────────────────────────

export interface TournamentMemberDay {
  userId: string;
  userName: string;
  starsEarned: number;   // delta for this specific day
  attacksDone: number;   // daily_attacks_done for this day
  membershipRank: string;
  trophies: number;
}

export interface TournamentDaySummary {
  dayNumber: number;     // 1–7
  date: string;          // YYYY-MM-DD
  hasData?: boolean;      // false/undefined when no snapshot was recorded for this day
  totalStars: number;
  totalAttacks: number;
  members: TournamentMemberDay[];
}

export interface TournamentTopEarner {
  userId: string;
  userName: string;
  totalStars: number;
}

export interface TournamentMissingAttack {
  userId: string;
  userName: string;
  totalAttacks: number;
}

export interface TournamentSummary {
  allianceId: string;
  allianceName: string;
  allianceDivision?: string;
  month: number;
  year: number;
  tournamentStart: string;
  tournamentEnd: string;
  memberCount: number;
  totalStars: number;
  attacksAvailable: number;    // memberCount × 42 (7 days × 6/day)
  attacksUsed: number;
  percentageUsed: number;
  trophyMedian: number;
  trophyAvg: number;
  trophyMax: number;
  starsPerAttack: number;
  starsPerActiveCaptain: number;
  topEarners: TournamentTopEarner[];
  missingAttacks: TournamentMissingAttack[];
  days: TournamentDaySummary[];
}

// ── Tournament forecast interfaces ──────────────────────────────────────────

export type ForecastConfidence = 'high' | 'medium' | 'low';

export interface ForecastMember {
  userId:              string;
  userName:            string;
  isNewMember:         boolean;
  priorStars:          number;
  priorAttacks:        number;
  adjustmentReason:    string;
  adjustmentFactor:    number;
  predictedStars:      number;
  predictedStarsLow:   number;
  predictedStarsHigh:  number;
  confidence:          ForecastConfidence;
}

export interface TournamentForecast {
  allianceId:           string;
  forecastMonth:        number;
  forecastYear:         number;
  priorTournamentStart: string;
  priorTournamentEnd:   string;
  memberCount:          number;
  existingMemberCount:  number;
  newMemberCount:       number;
  fleetPriorAvgStars:   number;
  predictedTotalStars:  number;
  fleetCurrentDivision?: string;
  members:              ForecastMember[];
}

export interface BacktestForecastMember extends ForecastMember {
  actualStars:   number;
  actualAttacks: number;
  starsDiff:     number;
  attacksDiff:   number;
}

export interface TournamentForecastBacktest extends Omit<TournamentForecast, 'members'> {
  allianceName:          string;
  actualTotalStars:      number;
  actualTotalAttacks:    number;
  predictedTotalAttacks: number;
  members:               BacktestForecastMember[];
}

export interface YaDcUserHistoryEntry {
  date:           string;   // YYYY-MM-DD
  allianceId:     string;
  allianceName:   string;
  division:       string;
  trophies:       number;
  tournamentStars: number;
  dailyAttacks:   number;
  crewDonated:    number;
  crewReceived:   number;
  highestTrophy:  number;
  membershipRank: string;
}

export interface PssPlayerDetails {
  name: string;
  shipLevel: string;
  shipDesignId: string;
  trophies: string;
  highestTrophy: string;
  powerScore: string;
  minerals: string;
  gas: string;
  starbux: string;
  bux?: string;
  doves?: string;
  globalRanking?: string;
  globalRankTotalUsers?: string;
  globalRankTotalActiveUsers?: string;
  allianceId: string;
  fleetName: string;
  fleetMembership: string;
  lastLoginDate: string;
  creationDate: string;
  pvpAttackWins: string;
  pvpAttackLosses: string;
  pvpDefenceWins: string;
  pvpDefenceLosses: string;
  hasSubscription: boolean;
  frame?: string;
  pin?: string;
  ribbon?: string;
  crew: PssCrewMember[];
  characterActions: PssCharacterAction[];
  rooms: PssRoom[];
  roomActions: PssRoomAction[];
  starSystemDetails: PssStarSystemDetail[];
  battles: PssBattle[];
  inventory: PssInventoryItem[];
  boostGauge: PssBoostGaugeInfo | null;
  rushTargets: PssRushTarget[];
}

export interface AiSnapshot {
  snapshotId: string;
  name: string;
  rules: { conditionTypeId: string; actionTypeId: string }[];
  createdAt: string;
}

export interface AiLibraryShare {
  shareId: string;
  shareType: 'fleet' | 'player';
  sharedWithUserId: string | null;
  sharedWithAllianceId: string | null;
  sharedWithName: string | null;
}

/** A library entry that belongs to the current user. */
export interface AiLibrarySnapshot {
  libraryId: string;
  name: string;
  description: string;
  rules: { conditionTypeId: string; actionTypeId: string }[];
  createdAt: string;
  folderId: string | null;
  shares: AiLibraryShare[];
}

/** A folder grouping library entries, owned by the current user. */
export interface AiLibraryFolder {
  folderId: string;
  name: string;
  createdAt: string;
  shares: AiLibraryShare[];
  entries: AiLibrarySnapshot[];
}

/** A library entry shared with the current user by someone else. */
export interface AiSharedLibrarySnapshot {
  libraryId: string;
  ownerUserId: string;
  ownerName: string | null;
  name: string;
  description: string;
  rules: { conditionTypeId: string; actionTypeId: string }[];
  createdAt: string;
  /** True when access comes from a folder share rather than a direct entry share. */
  sharedViaFolder: boolean;
  folderName: string | null;
}

export interface PssRankHistoryEntry {
  ranking: string;
  totalUsers: string;
  totalActiveUsers: string;
  recordedAt: string;
}

export interface PssBattle {
  battleId: string;
  attackingUserId: string;
  defendingUserId: string;
  attackingShipName: string;
  defendingShipName: string;
  attackingAllianceName: string;
  defendingAllianceName: string;
  outcomeType: string;          // "Attacker Won" | "Defender Won" | etc.
  winTrophyResult: number;      // trophies gained by winner (taken from loser)
  loseTrophyResult: number;     // trophies gained by loser (rare, usually 0)
  battleType: string;           // "PVP", etc.
  isStarBattle: boolean;        // true = tournament star attack
  battleDate: string;           // ISO datetime
  battleEndDate: string;
  leagueType: string;           // "Legendary", "Standard", etc.
  attackerWinRewards: string;   // raw reward string when attacker wins
  attackerLoseRewards: string;
  defenderWinRewards: string;
  defenderLoseRewards: string;
  starsWon: number;             // score: parsed from rewards (tournament stars)
  xpWon: number;
  mineralsWon: number;
  gasWon: number;
}

export interface RedeemCodeReward {
  itemDesignId: string;
  quantity: number;
}

export interface RedeemCodeRecord {
  code: string;
  status: 'active' | 'expired' | 'invalid';
  createdByUserId?: string;
  lastUsedByUserId?: string;
  lastUsedAt?: string;
  errorMessage?: string;
  createdAt?: string;
  updatedAt?: string;
  rewards: RedeemCodeReward[];
}

export interface RedeemCodeResult {
  code: string;
  status: 'active' | 'expired' | 'invalid' | 'already_redeemed';
  errorMessage?: string | null;
  errorCode?: string | null;
  rewards: RedeemCodeReward[];
  xml?: string;
}

export interface FleetDailySnapshot {
  id: number;

  leagueType: string;           // "Legendary", "Standard", etc.
  attackerWinRewards: string;   // raw reward string when attacker wins
  attackerLoseRewards: string;
  defenderWinRewards: string;
  defenderLoseRewards: string;
  starsWon: number;             // score: parsed from rewards (tournament stars)
  xpWon: number;
  mineralsWon: number;
  gasWon: number;
}

export interface PssDonationHistoryItem {
  characterId: string;
  deploymentDate: string;
  characterName?: string | null;
  characterCustomName?: string | null;
  userName?: string | null;
  lastBorrowedBy?: string | null;
  lastBorrowedAt?: string | null;
}

export interface PssPollingTask {
  id: number;
  taskKey: string;
  description: string;
  taskType: string;
  targetId: string | null;
  ownerAccountId: number | null;
  scheduleType: 'interval' | 'cron' | 'times';
  intervalSec: number;
  cronExpression: string | null;
  scheduleTimes: string[];
  isActive: boolean;
  lastRunAt: string | null;
  createdAt: string;
  updatedAt: string;
}

// Lift room design IDs — characters assigned here are undeployed and donatable
const LIFT_DESIGN_IDS = new Set(['3','248','249','250','251','252','253','264','265','266','267','268','419','420','536']);

@Injectable({ providedIn: 'root' })
export class PssAuthService {
  currentSession: PssSession | null = null;
  lastAuthTime = 0;
  private readonly TOKEN_CACHE_MS = 2 * 60 * 1000; // 2 minutes
  private aiMutationChain: Promise<void> = Promise.resolve();
  private reauthInFlight$: Observable<PssSession> | null = null;

  private characterDesigns$: Observable<Map<string, PssCharacterDesign>> | null = null;
  private roomDesigns$: Observable<Map<string, PssRoomDesign>> | null = null;
  private shipDesigns$: Observable<Map<string, PssShipDesign>> | null = null;
  private researchDesigns$: Observable<Map<string, PssResearchDesign>> | null = null;
  private itemDesigns$: Observable<Map<string, PssItemDesign>> | null = null;
  private ammoDesigns$: Observable<Map<string, PssAmmoDesign>> | null = null;
  private conditionTypes$: Observable<Map<string, PssConditionType>> | null = null;
  private actionTypes$: Observable<Map<string, PssActionType>> | null = null;

  constructor(private http: HttpClient, @Optional() private spriteCache?: SpriteCacheService) {
    // Kick off sprite metadata fetch as soon as the app starts so the dashboard
    // can render images immediately after login. The cache service is optional
    // for unit tests that construct this service directly.
    this.spriteCache?.ensureLoaded();
  }

  /**
   * What a room can be told to load or build, in the order the AI's
   * "Set Item {N}" actions index into. N is an OrderIndex over the room's own
   * pool, so index 0 is the first entry here, index 1 the second, up to {5}.
   *
   * A room's pool is every design whose ItemType matches what the room
   * manufactures and whose Rank matches the room's ItemRank, with the Lv2-6
   * upgrade variants collapsed back onto the base design they upgrade from.
   * Weapon rooms draw ammo, hangars draw craft, android studios draw droids.
   *
   * Rooms with a hard-wired MissileDesignId (Hwacha, Bolter) fire one fixed
   * missile and have no choice to make, so they get an empty pool.
   *
   * Note the in-game picker renders this pool as a 3-wide grid filled from the
   * bottom row up, so reading it top-to-bottom does NOT give this order.
   */
  static itemsForRoom(design: PssRoomDesign, itemDesigns: Map<string, PssAmmoDesign>): PssAmmoDesign[] {
    if (!ROOM_BUILDABLE_ITEM_TYPES.has(design.manufactureType)) return [];
    if (design.missileDesignId && design.missileDesignId !== '0') return [];
    const rank = design.itemRank;
    if (!rank || rank === '0') return [];

    const byRoot = new Map<string, PssAmmoDesign>();
    for (const item of itemDesigns.values()) {
      if (item.rank !== rank || item.itemType !== design.manufactureType) continue;
      const root = item.rootItemDesignId || item.id;
      const existing = byRoot.get(root);
      if (!existing || parseInt(item.id, 10) < parseInt(existing.id, 10)) byRoot.set(root, item);
    }
    return Array.from(byRoot.values()).sort((a, b) => parseInt(a.id, 10) - parseInt(b.id, 10));
  }

  static calculateBoostAmountSeconds(lastBoostDate: string, nowMs: number = Date.now(), fallbackSeconds = 0): number {
    const cap = 12 * 3600;
    const normalized = lastBoostDate ? (lastBoostDate.endsWith('Z') ? lastBoostDate : lastBoostDate + 'Z') : '';
    if (!normalized || normalized.startsWith('2000-01-01') || normalized.startsWith('1900-01-01')) {
      return Math.min(cap, Math.max(0, fallbackSeconds));
    }
    const ms = new Date(normalized).getTime();
    if (isNaN(ms) || ms <= 0) {
      return Math.min(cap, Math.max(0, fallbackSeconds));
    }
    const secs = Math.max(0, Math.floor((nowMs - ms) / 1000));
    return Math.min(cap, secs);
  }

  // ── Per-email device key persistence ─────────────────────────────────────
  // PSS's DeviceLogin17 registers a BRAND-NEW anonymous device account whenever
  // it sees an unknown deviceKey. Generating a fresh UUID on every login meant
  // we minted a new PSS device on every attempt — and since all traffic egresses
  // from a small pool of shared Cloudflare IPs, the whole user base collectively
  // tripped PSS's "too many accounts created in 24 hours" per-IP limit (and the
  // resulting PSS overload produced 502s).
  //
  // Persisting one key PER EMAIL fixes this: returning users reuse their existing
  // device instead of creating a new one, while each account keeps its own key so
  // there's no cross-account re-link corruption (the reason the old code avoided
  // reusing a single shared key).
  private deviceKeyForEmail(email: string): { key: string; isNew: boolean; keyCount: number } {
    const normalized = (email || '').trim().toLowerCase();
    if (!normalized) return { key: crypto.randomUUID(), isNew: true, keyCount: 0 };
    try {
      const raw = localStorage.getItem('pss_device_keys');
      const map: Record<string, string> = raw ? JSON.parse(raw) : {};
      let key = map[normalized];
      const isNew = !key;
      if (!key) {
        key = crypto.randomUUID();
        map[normalized] = key;
        localStorage.setItem('pss_device_keys', JSON.stringify(map));
      }
      return { key, isNew, keyCount: Object.keys(map).length };
    } catch {
      // localStorage unavailable (private mode / quota) — fall back to a fresh key.
      return { key: crypto.randomUUID(), isNew: true, keyCount: 0 };
    }
  }

  // Replaces the stored key for this email with a new one. Used when PSS refuses
  // to link an existing, never-successfully-linked device (see login()).
  private resetDeviceKeyForEmail(email: string): string {
    const normalized = (email || '').trim().toLowerCase();
    const key = crypto.randomUUID();
    if (!normalized) return key;
    try {
      const raw = localStorage.getItem('pss_device_keys');
      const map: Record<string, string> = raw ? JSON.parse(raw) : {};
      map[normalized] = key;
      localStorage.setItem('pss_device_keys', JSON.stringify(map));
    } catch { /* localStorage unavailable — the key still works for this attempt */ }
    return key;
  }

  // ── Login failure diagnostics ────────────────────────────────────────────
  // Browser-direct login never touches our Worker, so a PSS rejection would
  // otherwise leave no trace server-side. Most users are on mobile with no
  // console, so on any failed login we POST a secret-free report to
  // /api/login-diag (D1: login_diagnostics). Never includes the password,
  // tokens, or the unmasked email.
  private static maskEmail(email: string): string {
    const e = (email || '').trim().toLowerCase();
    const at = e.indexOf('@');
    if (at <= 0) return e ? '***' : '';
    return e[0] + '***' + e.slice(at);
  }

  private static redactXml(xml: string): string {
    return String(xml || '')
      .replace(/(accessToken|refreshToken)="[^"]*"/gi, '$1="[redacted]"')
      .slice(0, 2000);
  }

  private reportLoginDiag(err: any, base: Record<string, unknown>): void {
    const body = err?.error && typeof err.error === 'object' ? err.error : null;
    const payload = {
      ...base,
      ...(err?.diag || {}),
      step:      body?.step ?? err?.step ?? null,
      errorCode: body?.errorCode ?? err?.errorCode ?? null,
      message:   String(body?.error ?? err?.message ?? 'Login failed.').slice(0, 500),
      httpStatus: err?.status ?? null,
      xml:       PssAuthService.redactXml(body?.xml ?? err?.xml ?? ''),
      userAgent: (typeof navigator !== 'undefined' && navigator.userAgent) || '',
    };
    try {
      fetch('/api/login-diag', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
        keepalive: true,
      }).catch(() => { /* best effort */ });
    } catch { /* best effort */ }
  }

  // ── Email + password login ───────────────────────────────────────────────
  // Runs the PSS login sequence DIRECTLY FROM THE BROWSER so each login egresses
  // from the user's own IP — not Cloudflare's shared egress IPs, which the whole
  // user base collectively used to hammer, tripping PSS's per-IP "too many
  // accounts created in 24 hours" device-creation cap. Falls back to the
  // server-side /api/login proxy if the direct attempt fails (e.g. a network that
  // blocks the PSS API).
  login(email: string, password: string): Observable<PssSession> {
    // Reuse a persistent per-email device key instead of minting a new PSS
    // device on every login. See deviceKeyForEmail() for the full rationale.
    const { key, isNew: deviceKeyNew, keyCount: deviceKeyCount } = this.deviceKeyForEmail(email!);
    const diagBase = { emailMasked: PssAuthService.maskEmail(email), deviceKeyNew, deviceKeyCount };

    type RawLogin = { xml: string; server: string; accessToken: string; shipXml?: string; userDataXml?: string };
    const finish = (raw: RawLogin, deviceKey: string): PssSession => {
      const s = this.parseSession(raw.xml, deviceKey, raw.server, raw.accessToken);
      s.shipXml = raw.shipXml || '';
      s.userDataXml = raw.userDataXml || '';
      return s;
    };

    const serverFallback$ = (deviceKey: string, diag: Record<string, unknown>) =>
      this.http.post<RawLogin>('/api/login', { email, password, deviceKey }).pipe(
        map(raw => finish(raw, deviceKey)),
        catchError(err => {
          this.reportLoginDiag(err, { ...diag, path: 'proxy-fallback' });
          return throwError(() => err);
        }),
      );

    const attempt$ = (deviceKey: string, diag: Record<string, unknown>, path: string) =>
      from(this.loginDirectFromBrowser(email, password, deviceKey)).pipe(
        map(raw => finish(raw, deviceKey)),
        catchError(err => {
          // A definitive PSS auth/rate-limit answer is NOT a reason to retry via
          // the (shared-IP) server proxy — that would only re-trip the limit and
          // hide the real message. Only fall back on transport-level failures.
          if (err?.pssDefinitive) {
            this.reportLoginDiag(err, { ...diag, path });
            return throwError(() => err);
          }
          console.warn('[login] browser-direct failed, falling back to /api/login:', err?.message ?? err);
          return serverFallback$(deviceKey, diag);
        }),
      );

    // Once a login succeeds, the stored per-email key is linked to that account.
    // A later email login sends it to DeviceLogin17 with no refresh token, and PSS
    // refuses with errorCode 400 + the masked email and no access token
    // (login_diagnostics rows 21-23, 28). Start over on a fresh, unlinked key.
    return attempt$(key, diagBase, 'browser-direct').pipe(
      catchError(err => {
        if (!err?.deviceLinked) return throwError(() => err);
        console.warn('[login] stored device key is linked to an account, retrying with a fresh one');
        const freshKey = this.resetDeviceKeyForEmail(email);
        return attempt$(freshKey, { ...diagBase, deviceKeyNew: true }, 'browser-direct-fresh-key');
      }),
      tap(session => {
        this.currentSession = session;
        this.lastAuthTime = Date.now();
      }),
      switchMap(session => this.upsertAccount(session).pipe(map(() => session)))
    );
  }

  // Browser-direct PSS login. Mirrors the server-side handleLogin() flow but runs
  // in the user's browser (their IP). Returns the same shape /api/login returns.
  private async loginDirectFromBrowser(
    email: string,
    password: string,
    deviceKey: string,
  ): Promise<{ xml: string; server: string; accessToken: string; shipXml: string; userDataXml: string }> {
    const server = await this.getProductionServerDirect();
    // Diagnostic context attached to any error thrown below (see reportLoginDiag).
    const diag: Record<string, unknown> = { server };
    const t0 = Date.now();

    // Step 1 — DeviceLogin17 (anonymous device access token)
    const ts1  = pssTimestamp();
    const cs1  = deviceLoginChecksum(deviceKey, ts1);
    let xml1: string;
    try {
      xml1 = await this.deviceLoginDirect(server, deviceKey, ts1, cs1, null);
    } catch (e: any) {
      throw Object.assign(new Error(`Network error reaching Pixel Starships (DeviceLogin17): ${e?.message ?? e}`), { step: 'DeviceLogin17-network', diag });
    }
    diag['step1Ms'] = Date.now() - t0;
    // Identity of the anonymous user PSS bound to this device. A brand-new
    // device shows up as a fresh Unverified user with starter resources.
    const step1User = xml1.match(/<User\s[^>]*>/)?.[0] ?? '';
    diag['step1UserId']   = (xml1.match(/\bUserId="(\d+)"/) || [])[1] ?? null;
    diag['step1UserType'] = (step1User.match(/UserType="([^"]+)"/) || [])[1] ?? null;
    diag['step1NewUser']  = /Credits="100"/.test(step1User) && /Trophy="1000"/.test(step1User);
    const accessTokenMatch = xml1.match(/accessToken="([^"]+)"/);
    if (!accessTokenMatch) {
      const errMsg = (xml1.match(/errorMessage="([^"]+)"/) || [])[1] || 'DeviceLogin17 failed';
      const errorCode = (xml1.match(/errorCode="([^"]+)"/) || [])[1] || null;
      const isRateLimited = /many accounts|24 hours|short period/i.test(errMsg);
      const friendly = isRateLimited
        ? 'Pixel Starships is temporarily limiting new device logins (try again in up to 24 hours). If you have logged in before, use your saved account from the account picker instead — that does not hit this limit.'
        : errMsg;
      // Mark definitive so login() does NOT fall back to the server proxy.
      console.error('[login] DeviceLogin17 raw response:', xml1);
      const deviceLinked = errorCode === '400' && /<User\s[^>]*\bId="\d+"/.test(xml1);
      throw Object.assign(new Error(friendly), { pssDefinitive: true, step: 'DeviceLogin17', errorCode, pssMessage: errMsg, xml: xml1, diag, deviceLinked });
    }
    const accessToken = accessTokenMatch[1];

    // Step 2 — UserEmailPasswordAuthorize4 (password goes straight to PSS)
    const ts2 = pssTimestamp();
    const cs2 = emailPasswordChecksum(deviceKey, email, ts2, accessToken);
    const step2 = `https://${server}/UserService/UserEmailPasswordAuthorize4` +
      '?clientDateTime=' + encodeURIComponent(ts2) +
      '&checksum=' + cs2 +
      '&deviceKey=' + encodeURIComponent(deviceKey) +
      '&email=' + encodeURIComponent(email) +
      '&password=' + encodeURIComponent(password) +
      '&accessToken=' + encodeURIComponent(accessToken) +
      '&languageKey=en&isWeb=True';
    let xml2: string;
    const t1 = Date.now();
    try {
      xml2 = await this.fetchTextDirect(step2, 'POST');
    } catch (e: any) {
      throw Object.assign(new Error(`Network error reaching Pixel Starships (UserEmailPasswordAuthorize4): ${e?.message ?? e}`), { step: 'UserEmailPasswordAuthorize4-network', diag });
    }
    diag['step2Ms'] = Date.now() - t1;
    const errorMatch = xml2.match(/errorMessage="([^"]+)"/);
    const userIdMatch = xml2.match(/UserId="(\d+)"/) || xml2.match(/Id="(\d+)"/);
    const errorCodeMatch = xml2.match(/errorCode="([^"]+)"/);
    if (errorMatch && errorMatch[1] && !userIdMatch) {
      // Same shape as the /api/login error JSON (xml included): PSS's generic
      // "An error occurred." carries no errorCode, so the raw response is the
      // only thing that makes a bug report diagnosable.
      console.error('[login] UserEmailPasswordAuthorize4 raw response:', xml2);
      throw Object.assign(new Error(errorMatch[1]), {
        pssDefinitive: true, step: 'UserEmailPasswordAuthorize4', errorCode: errorCodeMatch?.[1] ?? null, xml: xml2, diag,
      });
    }
    if (errorMatch && errorMatch[1] && userIdMatch) {
      // PSS attached a benign errorMessage (often a masked email, errorCode="400")
      // alongside a valid UserId — same non-fatal case parseSession() already
      // handles. Log it so it's diagnosable without looking like a hard failure.
      console.warn('[login] step2 non-fatal errorMessage alongside valid UserId:', errorMatch[1], 'errorCode=', errorCodeMatch?.[1]);
    }

    const userId = userIdMatch && userIdMatch[1];
    if (!userId) {
      // No userId but no error — return what we have; parseSession will sort it out.
      return { xml: xml2, server, accessToken, shipXml: '', userDataXml: '' };
    }

    // Step 3 — second DeviceLogin17 with the refresh token → owner access token
    const refreshTokenMatch = xml2.match(/refreshToken="([^"]+)"/);
    const refreshToken3 = refreshTokenMatch ? refreshTokenMatch[1] : null;
    const ts3 = pssTimestamp();
    const cs3 = deviceLoginChecksum(deviceKey, ts3);
    let xml3: string | null = null;
    try { xml3 = await this.deviceLoginDirect(server, deviceKey, ts3, cs3, refreshToken3); } catch { /* non-fatal */ }
    const ownerTokenMatch = xml3 && xml3.match(/accessToken="([^"]+)"/);
    const ownerToken = ownerTokenMatch ? ownerTokenMatch[1] : accessToken;

    // Step 4 — GetShipByUserId + ListAllUserDataFirst2 in parallel
    const clientDateTime2 = new Date().toISOString().slice(0, 19);
    const shipUrl = `https://${server}/ShipService/GetShipByUserId?userId=${userId}` +
      '&accessToken=' + encodeURIComponent(ownerToken) +
      '&clientDateTime=' + encodeURIComponent(clientDateTime2) + '&checksum=0';
    const userDataUrl = `https://${server}/UserService/ListAllUserDataFirst2?userId=${userId}` +
      '&accessToken=' + encodeURIComponent(ownerToken);
    const [shipResult, userDataResult] = await Promise.allSettled([
      this.fetchTextDirect(shipUrl, 'GET'),
      this.fetchTextDirect(userDataUrl, 'GET'),
    ]);

    return {
      xml: xml2,
      server,
      accessToken: ownerToken,
      shipXml:     shipResult.status === 'fulfilled'     ? (shipResult.value     || '') : '',
      userDataXml: userDataResult.status === 'fulfilled' ? (userDataResult.value || '') : '',
    };
  }

  private async getProductionServerDirect(): Promise<string> {
    try {
      const xml = await this.fetchTextDirect(
        'https://api.pixelstarships.com/SettingService/getlatestversion3?languageKey=en&deviceType=DeviceTypeAndroid',
        'GET',
      );
      const m = xml.match(/ProductionServer="([^"]+)"/);
      return m ? m[1] : 'api2.pixelstarships.com';
    } catch {
      return 'api2.pixelstarships.com';
    }
  }

  // DeviceLogin17 is a POST that carries both query params and a JSON body.
  private async deviceLoginDirect(
    server: string, deviceKey: string, ts: string, checksum: string, refreshToken: string | null,
  ): Promise<string> {
    const url  = `https://${server}` + buildDeviceLoginPath(deviceKey, ts, checksum, refreshToken);
    const body = JSON.stringify(buildDeviceLoginBody(deviceKey, ts, checksum, refreshToken));
    return this.fetchTextDirect(url, 'POST', body, { 'Content-Type': 'application/json' });
  }

  private async fetchTextDirect(
    url: string, method: 'GET' | 'POST', body?: string, headers?: Record<string, string>,
  ): Promise<string> {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), 20000);
    try {
      const resp = await fetch(url, {
        method,
        headers: method === 'POST' ? { 'Content-Type': 'application/x-www-form-urlencoded', ...(headers || {}) } : (headers || {}),
        body: method === 'POST' ? (body ?? '') : undefined,
        signal: controller.signal,
      });
      return await resp.text();
    } finally {
      clearTimeout(timer);
    }
  }

  // ── Device/refresh token login ────────────────────────────────────────────
  loginWithAccount(account: PssAccount): Observable<PssSession> {
    return this.http.post<{ xml: string; server: string; accessToken?: string; shipXml?: string; userDataXml?: string }>(
      '/api/device', { deviceKey: account.deviceKey, refreshToken: account.refreshToken }
    ).pipe(
      map(({ xml, server, accessToken, shipXml, userDataXml }) => {
        const s = this.parseSession(xml, account.deviceKey, server, accessToken || account.refreshToken);
        // Bug 1 fix: DeviceLogin17 XML does NOT return refreshToken — preserve
        // the one we sent so reauthenticate() can reuse it on subsequent calls.
        if (!s.refreshToken && account.refreshToken) {
          s.refreshToken = account.refreshToken;
        }
        s.shipXml = shipXml || '';
        s.userDataXml = userDataXml || '';
        return s;
      }),
      tap(session => {
        this.currentSession = session;
        this.lastAuthTime = Date.now();
      }),
      switchMap(session => this.upsertAccount(session).pipe(map(() => session)))
    );
  }

  // ── Silent re-authentication using the current session's device credentials ─
  reauthenticate(): Observable<PssSession> {
    const s = this.currentSession;
    if (!s) return throwError(() => new Error('No active session'));
    const account: PssAccount = {
      userId:       s.userId,
      name:         s.name,
      shipLevel:    s.shipLevel,
      deviceKey:    s.deviceKey,
      refreshToken: s.refreshToken,
      lastLogin:    new Date().toISOString(),
    };
    return this.loginWithAccount(account);
  }

  // ── Ensure a fresh session before every API call ─────────────────────────
  // Returns the current session immediately if it was obtained within the last
  // TOKEN_CACHE_MS (2 min), otherwise performs a full device re-login first.
  // Falls back to localStorage if currentSession is null (e.g. after page reload
  // with a lost in-memory session).
  // Concurrent callers share a single in-flight reauth to prevent races.
  ensureFreshSession(): Observable<PssSession> {
    if (this.currentSession && this.currentSession.accessToken && Date.now() - this.lastAuthTime < this.TOKEN_CACHE_MS) {
      return of(this.currentSession);
    }

    // If a reauth is already in-flight, piggyback on it
    if (this.reauthInFlight$) {
      return this.reauthInFlight$;
    }

    let source$: Observable<PssSession>;

    if (this.currentSession) {
      source$ = this.reauthenticate();
    } else {
      // currentSession is null — try to restore from localStorage
      try {
        const raw = localStorage.getItem('pss_accounts');
        const accounts: PssAccount[] = raw ? JSON.parse(raw) : [];
        if (accounts.length > 0) {
          const mostRecent = accounts.slice().sort((a, b) => b.lastLogin.localeCompare(a.lastLogin))[0];
          source$ = this.loginWithAccount(mostRecent);
        } else {
          return throwError(() => new Error('No saved account — please log in'));
        }
      } catch {
        return throwError(() => new Error('No saved account — please log in'));
      }
    }

    // Share a single in-flight reauth; clear reference when done
    this.reauthInFlight$ = source$.pipe(
      tap({
        next: session => {
          // Guard: reject sessions with empty accessToken
          if (!session.accessToken) {
            console.error('[ensureFreshSession] reauth returned empty accessToken');
          }
          this.reauthInFlight$ = null;
        },
        error: () => { this.reauthInFlight$ = null; },
      }),
      share()
    );
    return this.reauthInFlight$;
  }

  // ── Full player details ───────────────────────────────────────────────────
  getPlayerDetails(session: PssSession): Observable<PssPlayerDetails> {
    const token  = session.accessToken ? encodeURIComponent(session.accessToken) : '';
    const userId = session.userId;
    const server = session.productionServer;

    const userData$ = token ? this.http.get(
      `/pss/${server}/UserService/ListAllUserDataFirst2?userId=${userId}&accessToken=${token}`,
      { responseType: 'text' }
    ).pipe(catchError(() => of(''))) : of('');

    const promotions$ = token ? this.http.post(
      `/pss/api.pixelstarships.com/PromotionService/FixUserPromotions?accessToken=${token}`,
      null,
      { responseType: 'text' }
    ).pipe(catchError(() => of(''))) : of('');

    if (session.loginXml) {
      // If the server already fetched ship XML (device login path), use it directly.
      if (session.shipXml) {
        // userDataXml already fetched server-side; no extra request needed.
        const ud = session.userDataXml || '';
        if (ud) {
          return promotions$.pipe(
            map(px => this.parsePlayerDetails(session.loginXml, session.shipXml!, ud, px, userId))
          );
        }
        // Fall back: fetch userDataXml client-side if server didn't provide it.
        return forkJoin({ ud: userData$, px: promotions$ }).pipe(
          map(({ ud, px }) => this.parsePlayerDetails(session.loginXml, session.shipXml!, ud, px, userId))
        );
      }
      // Email+password login has a real accessToken — fetch ship + userdata.
      if (token) {
        const clientDateTime = encodeURIComponent(new Date().toISOString().slice(0, 19));
        const ship$ = this.http.get(
          `/pss/${server}/ShipService/GetShipByUserId?userId=${userId}&accessToken=${token}&clientDateTime=${clientDateTime}&checksum=0`,
          { responseType: 'text' }
        );
        return forkJoin({ ship: ship$, ud: userData$, px: promotions$ }).pipe(
          map(({ ship, ud, px }) => this.parsePlayerDetails(session.loginXml, ship, ud, px, userId)),
          catchError(() => of(this.parsePlayerDetails(session.loginXml, '', '', '', userId)))
        );
      }
      return promotions$.pipe(
        map(px => this.parsePlayerDetails(session.loginXml, '', '', px, userId))
      );
    }

    // Full API fallback (no loginXml — e.g. older saved sessions)
    if (!token) return of(this.parsePlayerDetails('', '', '', '', userId));
    const user$ = this.http.get(
      `/pss/${server}/UserService/GetUserById4?userId=${userId}&accessToken=${token}&checksum=0`,
      { responseType: 'text' }
    );
    const clientDateTimeFallback = encodeURIComponent(new Date().toISOString().slice(0, 19));
    const ship$ = this.http.get(
      `/pss/${server}/ShipService/GetShipByUserId?userId=${userId}&accessToken=${token}&clientDateTime=${clientDateTimeFallback}&checksum=0`,
      { responseType: 'text' }
    );
    return forkJoin({ user: user$, ship: ship$, ud: userData$, px: promotions$ }).pipe(
      map(({ user, ship, ud, px }) => this.parsePlayerDetails(user, ship, ud, px, userId))
    );
  }

  // ── Account list ──────────────────────────────────────────────────────────
  getSavedAccounts(): Observable<PssAccount[]> {
    try {
      const raw = localStorage.getItem('pss_accounts');
      return of(raw ? JSON.parse(raw) as PssAccount[] : []);
    } catch {
      return of([]);
    }
  }

  removeAccount(userId: string): Observable<unknown> {
    try {
      const raw = localStorage.getItem('pss_accounts');
      const accounts: PssAccount[] = raw ? JSON.parse(raw) : [];
      localStorage.setItem('pss_accounts', JSON.stringify(accounts.filter(a => a.userId !== userId)));
    } catch {}
    return of(null);
  }

  logout(): void {
    this.currentSession = null;
    this.characterDesigns$ = null;
    this.roomDesigns$ = null;
    this.shipDesigns$ = null;
    this.itemDesigns$ = null;
    this.conditionTypes$ = null;
    this.actionTypes$ = null;
  }

  // ── Character designs (fetched once, cached for session) ─────────────────
  getCharacterDesigns(server: string): Observable<Map<string, PssCharacterDesign>> {
    if (!this.characterDesigns$) {
      this.characterDesigns$ = this.http.get(
        `/pss/${server}/CharacterService/ListAllCharacterDesigns2?languageKey=en`,
        { responseType: 'text' }
      ).pipe(
        map(xml => {
          const doc = new DOMParser().parseFromString(xml, 'text/xml');
          const map = new Map<string, PssCharacterDesign>();
          doc.querySelectorAll('CharacterDesign').forEach(el => {
            const id = el.getAttribute('CharacterDesignId') ?? '';
            map.set(id, {
              id,
              name:               el.getAttribute('CharacterDesignName') ?? id,
              // No 'Common' fallback: destructive flows (fire 1-2 star crew) must
              // treat a missing Rarity attribute as unknown, never as 1-star.
              rarity:             el.getAttribute('Rarity') ?? '',
              profileSpriteId:    el.getAttribute('ProfileSpriteId') ?? '',
              finalHp:            el.getAttribute('FinalHp') ?? '0',
              finalPilot:         el.getAttribute('FinalPilot') ?? '0',
              finalAttack:        el.getAttribute('FinalAttack') ?? '0',
              finalWeapon:        el.getAttribute('FinalWeapon') ?? '0',
              finalScience:       el.getAttribute('FinalScience') ?? '0',
              finalEngine:        el.getAttribute('FinalEngine') ?? '0',
              finalAbility:       el.getAttribute('FinalAbility') ?? '0',
              finalRepair:        el.getAttribute('FinalRepair') ?? '0',
              maxLevel:           el.getAttribute('MaxCharacterLevel') ?? '40',
              specialAbilityType: el.getAttribute('SpecialAbilityType') ?? '',
              specialAbilityArg:  el.getAttribute('SpecialAbilityFinalArgument') ?? '0',
            });
          });
          return map;
        }),
        catchError(() => of(new Map<string, PssCharacterDesign>())),
        shareReplay(1)
      );
    }
    return this.characterDesigns$;
  }

  // ── Room designs (fetched once, cached for session) ───────────────────────
  getRoomDesigns(server: string): Observable<Map<string, PssRoomDesign>> {
    if (!this.roomDesigns$) {
      this.roomDesigns$ = this.http.get(
        `/pss/${server}/RoomService/ListRoomDesigns2?languageKey=en`,
        { responseType: 'text' }
      ).pipe(
        map(xml => {
          const doc = new DOMParser().parseFromString(xml, 'text/xml');
          const map = new Map<string, PssRoomDesign>();
          doc.querySelectorAll('RoomDesign').forEach(el => {
            const id = el.getAttribute('RoomDesignId') ?? '';
            const name = el.getAttribute('RoomName') ?? el.getAttribute('RoomDesignName') ?? id;
            if (id) map.set(id, {
              id,
              name,
              rows:             el.getAttribute('Rows') ?? '1',
              cols:             el.getAttribute('Columns') ?? '1',
              roomType:         el.getAttribute('RoomType') ?? '',
              imageSpriteId:    el.getAttribute('ImageSpriteId') ?? '',
              flags:            parseInt(el.getAttribute('Flags') ?? '0', 10),
              constructionTime: parseInt(el.getAttribute('ConstructionTime') ?? '0', 10),
              reloadTime:      el.getAttribute('ReloadTime') ?? '0',
              minDamage:       el.getAttribute('MinSystemDamage') ?? el.getAttribute('MinDamage') ?? '0',
              maxDamage:       el.getAttribute('MaxSystemDamage') ?? el.getAttribute('MaxDamage') ?? '0',
              capacity:        el.getAttribute('Capacity') ?? '0',
              enhancementType: el.getAttribute('EnhancementType') ?? '',
              missileDesignId: el.getAttribute('MissileDesignId') ?? '0',
              itemRank:        el.getAttribute('ItemRank') ?? '0',
              manufactureType: el.getAttribute('ManufactureType') ?? 'None',
            });
          });
          return map;
        }),
        catchError(() => of(new Map<string, PssRoomDesign>())),
        shareReplay(1)
      );
    }
    return this.roomDesigns$;
  }

  // ── Ammo designs — missile items from ItemService (fetched once, cached) ──
  getAmmoDesigns(server: string): Observable<Map<string, PssAmmoDesign>> {
    if (!this.ammoDesigns$) {
      this.ammoDesigns$ = this.http.get(
        `/pss/${server}/ItemService/ListItemDesigns2?languageKey=en`,
        { responseType: 'text' }
      ).pipe(
        map(xml => {
          const doc = new DOMParser().parseFromString(xml, 'text/xml');
          const map = new Map<string, PssAmmoDesign>();
          doc.querySelectorAll('ItemDesign').forEach(el => {
            const itemType = el.getAttribute('ItemType') ?? '';
            if (!ROOM_BUILDABLE_ITEM_TYPES.has(itemType)) return;
            const id = el.getAttribute('ItemDesignId') ?? '';
            if (!id) return;
            map.set(id, {
              id,
              itemType,
              name:             el.getAttribute('ItemDesignName') ?? id,
              reloadModifier:   el.getAttribute('ReloadModifier') ?? '100',
              missileDesignId:  el.getAttribute('MissileDesignId') ?? '0',
              rank:             el.getAttribute('Rank') ?? '0',
              rootItemDesignId: el.getAttribute('RootItemDesignId') ?? id,
            });
          });
          return map;
        }),
        catchError(() => of(new Map<string, PssAmmoDesign>())),
        shareReplay(1)
      );
    }
    return this.ammoDesigns$;
  }

  // ── Ship designs (fetched once, cached for session) ───────────────────────
  getShipDesigns(server: string): Observable<Map<string, PssShipDesign>> {
    if (!this.shipDesigns$) {
      this.shipDesigns$ = this.http.get(
        `/pss/${server}/ShipService/ListAllShipDesigns2?languageKey=en`,
        { responseType: 'text' }
      ).pipe(
        map(xml => {
          const doc = new DOMParser().parseFromString(xml, 'text/xml');
          const m = new Map<string, PssShipDesign>();
          doc.querySelectorAll('ShipDesign').forEach(el => {
            const id = el.getAttribute('ShipDesignId') ?? '';
            if (id) m.set(id, {
              id,
              name:             el.getAttribute('ShipDesignName') ?? id,
              rows:             el.getAttribute('Rows') ?? '8',
              cols:             el.getAttribute('Columns') ?? '24',
              interiorSpriteId: el.getAttribute('InteriorSpriteId') ?? '',
              logoSpriteId:     el.getAttribute('LogoSpriteId') ?? '',
              upgradeTime:      parseInt(el.getAttribute('UpgradeTime') ?? '0', 10),
            });
          });
          return m;
        }),
        catchError(() => of(new Map<string, PssShipDesign>())),
        shareReplay(1)
      );
    }
    return this.shipDesigns$;
  }

  // ── Item designs (fetched once, cached for session) ───────────────────────
  getItemDesigns(server: string): Observable<Map<string, PssItemDesign>> {
    if (!this.itemDesigns$) {
      this.itemDesigns$ = this.http.get(
        `/pss/${server}/ItemService/ListItemDesigns2?languageKey=en`,
        { responseType: 'text' }
      ).pipe(
        map(xml => {
          const doc = new DOMParser().parseFromString(xml, 'text/xml');
          const m = new Map<string, PssItemDesign>();
          doc.querySelectorAll('ItemDesign').forEach(el => {
            const id = el.getAttribute('ItemDesignId') ?? '';
            if (id) m.set(id, {
              id,
              name:             el.getAttribute('ItemDesignName') ?? id,
              description:      el.getAttribute('ItemDesignDescription') ?? el.getAttribute('Description') ?? '',
              itemType:         el.getAttribute('ItemType') ?? '',
              subtype:          el.getAttribute('ItemSubType') ?? '',
              logoSpriteId:     el.getAttribute('LogoSpriteId') ?? '',
              rarity:           el.getAttribute('Rarity') ?? '',
              enhancementType:  el.getAttribute('EnhancementType') ?? '',
              modifier:         el.getAttribute('Modifier') ?? '',
              enhancementValue: el.getAttribute('EnhancementValue') ?? '',
              enhancementType2: el.getAttribute('EnhancementType2') ?? el.getAttribute('SubEnhancementType') ?? el.getAttribute('SecondaryEnhancementType') ?? el.getAttribute('AdditionalEnhancementType') ?? '',
              modifier2:        el.getAttribute('Modifier2') ?? el.getAttribute('SecondaryModifier') ?? el.getAttribute('AdditionalModifier') ?? '',
              enhancementValue2: el.getAttribute('EnhancementValue2') ?? el.getAttribute('SubEnhancementValue') ?? el.getAttribute('SecondaryEnhancementValue') ?? el.getAttribute('AdditionalEnhancementValue') ?? '',
              bonusEnhancementType: el.getAttribute('BonusEnhancementType') ?? el.getAttribute('bonusEnhancementType') ?? '',
              bonusEnhancementValue: el.getAttribute('BonusEnhancementValue') ?? el.getAttribute('bonusEnhancementValue') ?? '',
              bonusEnhancementType2: el.getAttribute('BonusEnhancementType2') ?? el.getAttribute('bonusEnhancementType2') ?? '',
              bonusEnhancementValue2: el.getAttribute('BonusEnhancementValue2') ?? el.getAttribute('bonusEnhancementValue2') ?? '',
              flags:            parseInt(el.getAttribute('Flags') ?? '0', 10),
            });
          });
          return m;
        }),
        catchError(() => of(new Map<string, PssItemDesign>())),
        shareReplay(1)
      );
    }
    return this.itemDesigns$;
  }

  // ── Research designs (best-effort, fetched once, cached for session) ─────
  getResearchDesigns(server: string): Observable<Map<string, PssResearchDesign>> {
    if (!this.researchDesigns$) {
      const candidates = [
        `/pss/${server}/ResearchService/ListAllResearchDesigns2?languageKey=en`,
        `/pss/${server}/ResearchService/ListResearchDesigns2?languageKey=en`,
        `/pss/${server}/ResearchService/ListAllResearchDesigns?languageKey=en`,
        `/pss/${server}/ResearchService/ListResearchDesigns?languageKey=en`,
      ];

      this.researchDesigns$ = forkJoin(
        candidates.map(url => this.http.get(url, { responseType: 'text' }).pipe(catchError(() => of(''))))
      ).pipe(
        map(xmls => {
          const xml = xmls.find(x => /<ResearchDesign\b/.test(x)) ?? '';
          if (!xml) return new Map<string, PssResearchDesign>();
          const doc = new DOMParser().parseFromString(xml, 'text/xml');
          const m = new Map<string, PssResearchDesign>();
          doc.querySelectorAll('ResearchDesign').forEach(el => {
            const id = el.getAttribute('ResearchDesignId') ?? '';
            if (!id) return;
            m.set(id, {
              id,
              name:         el.getAttribute('ResearchName') ?? el.getAttribute('ResearchDesignName') ?? el.getAttribute('Name') ?? ('Research #' + id),
              level:        el.getAttribute('Level') ?? el.getAttribute('ResearchLevel') ?? el.getAttribute('DesignLevel') ?? '',
              logoSpriteId: el.getAttribute('LogoSpriteId') ?? el.getAttribute('ImageSpriteId') ?? el.getAttribute('IconSpriteId') ?? '',
              researchTime: parseInt(el.getAttribute('ResearchTime') ?? '0', 10),
            });
          });
          return m;
        }),
        catchError(() => of(new Map<string, PssResearchDesign>())),
        shareReplay(1)
      );
    }
    return this.researchDesigns$;
  }

  // ── Condition types (crew AI, cached for session) ─────────────────────────
  getConditionTypes(server: string): Observable<Map<string, PssConditionType>> {
    if (!this.conditionTypes$) {
      this.conditionTypes$ = this.http.get(
        `/pss/${server}/RoomService/ListConditionTypes2?languageKey=en`,
        { responseType: 'text' }
      ).pipe(
        map(xml => {
          const doc = new DOMParser().parseFromString(xml, 'text/xml');
          const m = new Map<string, PssConditionType>();
          doc.querySelectorAll('ConditionType').forEach(el => {
            const id = el.getAttribute('ConditionTypeId') ?? '';
            if (id) m.set(id, { id, name: el.getAttribute('ConditionTypeName') ?? id, roomType: el.getAttribute('RoomType') ?? '' });
          });
          return m;
        }),
        catchError(() => of(new Map<string, PssConditionType>())),
        shareReplay(1)
      );
    }
    return this.conditionTypes$;
  }

  // ── Action types (crew AI, cached for session) ────────────────────────────
  getActionTypes(server: string): Observable<Map<string, PssActionType>> {
    if (!this.actionTypes$) {
      this.actionTypes$ = this.http.get(
        `/pss/${server}/RoomService/ListActionTypes2?languageKey=en`,
        { responseType: 'text' }
      ).pipe(
        map(xml => {
          const doc = new DOMParser().parseFromString(xml, 'text/xml');
          const m = new Map<string, PssActionType>();
          doc.querySelectorAll('ActionType').forEach(el => {
            const id = el.getAttribute('ActionTypeId') ?? '';
            if (id) m.set(id, { id, name: el.getAttribute('ActionTypeName') ?? id, key: el.getAttribute('ActionTypeKey') ?? '', roomType: el.getAttribute('RoomType') ?? '' });
          });
          return m;
        }),
        catchError(() => of(new Map<string, PssActionType>())),
        shareReplay(1)
      );
    }
    return this.actionTypes$;
  }

  // ── Star systems (fetched once, cached for session) ───────────────────────
  private starSystems$: Observable<Map<string, PssStarSystem>> | null = null;

  getStarSystems(server: string, accessToken: string): Observable<Map<string, PssStarSystem>> {
    if (!this.starSystems$) {
      const encodedToken = encodeURIComponent(accessToken);
      this.starSystems$ = this.http.get(
        `/pss/${server}/GalaxyService/ListStarSystems?accessToken=${encodedToken}`,
        { responseType: 'text' }
      ).pipe(
        map(xml => {
          const doc = new DOMParser().parseFromString(xml, 'text/xml');
          const m = new Map<string, PssStarSystem>();
          doc.querySelectorAll('StarSystem').forEach(el => {
            const id = el.getAttribute('StarSystemId') ?? '';
            const links: string[] = [];
            el.querySelectorAll('Link').forEach(link => {
              const toId = link.getAttribute('ToStarSystemId') ?? link.getAttribute('StarSystemId') ?? '';
              if (toId && toId !== id) links.push(toId);
            });
            const neighbors = el.getAttribute('Neighbors') ?? el.getAttribute('NeighborIds') ?? '';
            if (neighbors) neighbors.split(',').forEach(n => { const t = n.trim(); if (t && t !== id) links.push(t); });
            if (id) m.set(id, {
              id,
              title: el.getAttribute('StarSystemTitle') ?? id,
              x:     el.getAttribute('X') ?? '0',
              y:     el.getAttribute('Y') ?? '0',
              links,
            });
          });
          return m;
        }),
        catchError(() => of(new Map<string, PssStarSystem>())),
        shareReplay(1)
      );
    }
    return this.starSystems$;
  }

  getUserRanking(accessToken: string): Observable<{ ranking: string; totalUsers: string; totalActiveUsers: string }> {
    const token = encodeURIComponent(accessToken);
    return this.http.get(
      `/pss/api.pixelstarships.com/LadderService/FindUserRanking?accessToken=${token}`,
      { responseType: 'text' }
    ).pipe(
      map(xml => {
        const extract = (key: string) => {
          const match = new RegExp(`${key}="([^"]*)"`).exec(xml);
          return match ? match[1] : '';
        };
        return {
          ranking: extract('Ranking'),
          totalUsers: extract('TotalUsers'),
          totalActiveUsers: extract('TotalActiveUsers'),
        };
      }),
      catchError(() => of({ ranking: '', totalUsers: '', totalActiveUsers: '' }))
    );
  }

  getStoredUserRanking(userId: string): Observable<PssRankHistoryEntry | null> {
    if (!userId) return of(null);
    return this.http.get<PssRankHistoryEntry>(`/api/user-ranking?userId=${encodeURIComponent(userId)}`).pipe(
      map(resp => resp && resp.ranking ? resp : null),
      catchError(() => of(null))
    );
  }

  getUserRankingHistory(userId: string, limit = 20): Observable<PssRankHistoryEntry[]> {
    if (!userId) return of([]);
    return this.http.get<{ history: PssRankHistoryEntry[] }>(`/api/user-ranking/history?userId=${encodeURIComponent(userId)}&limit=${limit}`).pipe(
      map(resp => Array.isArray(resp.history) ? resp.history : []),
      catchError(() => of([]))
    );
  }

  saveUserRanking(userId: string, ranking: string, totalUsers: string, totalActiveUsers: string, recordedAt?: string): Observable<{ success: boolean }> {
    return this.http.post<{ success: boolean }>('/api/user-ranking', {
      userId,
      ranking,
      totalUsers,
      totalActiveUsers,
      recordedAt,
    }).pipe(
      catchError(() => of({ success: false }))
    );
  }

  // ── Internals ─────────────────────────────────────────────────────────────

  private parseSession(xml: string, deviceKey: string, server: string, fallbackAccessToken?: string): PssSession {
    const doc = new DOMParser().parseFromString(xml, 'text/xml');

    // DeviceLogin17 response:            <UserLogin accessToken="..." userId="..." ...>
    // UserEmailPasswordAuthorize4 resp:  <UserEmailPasswordAuthorize accessToken="..." refreshToken="..." UserId="...">
    //                                      <User Id="..." Name="..." ...>
    const authNode = doc.querySelector('UserLogin, UserEmailPasswordAuthorize');
    const userNode = doc.querySelector('User');

    // userId: DeviceLogin17 puts it on UserLogin; UserEmailPasswordAuthorize puts it as UserId on itself
    const userId = authNode?.getAttribute('userId')
      ?? authNode?.getAttribute('UserId')
      ?? userNode?.getAttribute('Id')
      ?? '';

    const accessToken = authNode?.getAttribute('accessToken') || fallbackAccessToken || '';

    // Fatal only when there is neither an access token nor any user data.
    // PSS returns errorCode="400" with a masked email (no accessToken) for devices already linked
    // to an email account — that is NOT a real failure; the User node still has all the data.
    if (!accessToken && !userId) {
      const errorMessage = authNode?.getAttribute('errorMessage')
        ?? doc.querySelector('Error')?.getAttribute('message');
      const errorCode = authNode?.getAttribute('errorCode')
        ?? doc.querySelector('Error')?.getAttribute('code')
        ?? null;
      throw Object.assign(new Error(errorMessage || 'Login failed – no access token in response.'), {
        step: 'parseSession', errorCode,
      });
    }

    // name and shipLevel live on the child <User> element
    const name      = userNode?.getAttribute('Name') ?? userNode?.getAttribute('name') ?? '';
    const shipLevel = userNode?.getAttribute('ShipLevel') ?? userNode?.getAttribute('shipLevel') ?? '';
    const frame     = userNode?.getAttribute('Frame') ?? userNode?.getAttribute('UserFrame') ?? '';
    const pin       = userNode?.getAttribute('Pin') ?? userNode?.getAttribute('UserPin') ?? '';
    const ribbon    = userNode?.getAttribute('Ribbon') ?? userNode?.getAttribute('UserRibbon') ?? '';

    const refreshToken = authNode?.getAttribute('refreshToken')
      ?? authNode?.getAttribute('RefreshToken')
      ?? '';

    return {
      accessToken,
      userId,
      name,
      shipLevel,
      deviceKey:        userNode?.getAttribute('deviceKey') ?? deviceKey,
      refreshToken,
      productionServer: server,
      loginXml:         xml,
      frame:            frame || undefined,
      pin:              pin || undefined,
      ribbon:           ribbon || undefined,
    };
  }

  private parsePlayerDetails(userXml: string, shipXml: string, userDataXml?: string, promotionsXml?: string, expectedUserId?: string): PssPlayerDetails {
    if (userDataXml) undefined;
    const uDoc  = userXml ? new DOMParser().parseFromString(userXml, 'text/xml') : null;
    const sDoc  = shipXml ? new DOMParser().parseFromString(shipXml, 'text/xml') : null;
    const pDoc  = promotionsXml ? new DOMParser().parseFromString(promotionsXml, 'text/xml') : null;
    const user  = uDoc?.querySelector('User') ?? uDoc?.documentElement ?? null;
    const primaryUserId = expectedUserId || (user?.getAttribute('Id') ?? user?.getAttribute('UserId') ?? '');
    const promoUsers = Array.from(pDoc?.querySelectorAll('FixUserPromotions > User, PromotionService User, User') ?? []);
    const promoUser =
      promoUsers.find(pu =>
        (pu.getAttribute('Id') ?? pu.getAttribute('UserId') ?? '') === primaryUserId
      ) ??
      promoUsers.find(pu => (pu.getAttribute('BoostAmount') ?? '') !== '') ??
      promoUsers[0] ??
      null;
    const ship  = sDoc?.querySelector('Ship') ?? null;  // never fall back to documentElement
    const fleet = uDoc?.querySelector('Alliance') ?? uDoc?.querySelector('Fleet') ?? null;
    // ra() returns null for missing attrs so ?? chains work correctly
    const ra    = (el: Element | null, k: string) => el?.getAttribute(k) ?? null;
    const attr  = (el: Element | null, k: string) => ra(el, k) ?? '—';
    const isPlaceholderDate = (iso: string): boolean =>
      !iso || iso.startsWith('2000-01-01') || iso.startsWith('1900-01-01');
    const toUtcMs = (iso: string): number => {
      if (isPlaceholderDate(iso)) return 0;
      const normalized = iso.endsWith('Z') ? iso : iso + 'Z';
      const ms = new Date(normalized).getTime();
      return isNaN(ms) ? 0 : ms;
    };
    const pickDateAttr = (el: Element | null, keys: string[]): string => {
      for (const k of keys) {
        const v = ra(el, k) ?? '';
        if (!isPlaceholderDate(v) && toUtcMs(v) > 0) return v;
      }
      return '';
    };
    const secondsToHhmm = (seconds: number): string => {
      const safe = Math.max(0, seconds | 0);
      const h = Math.floor(safe / 3600);
      const m = Math.floor((safe % 3600) / 60);
      return String(h).padStart(2, '0') + ':' + String(m).padStart(2, '0');
    };

    // Minerals = ItemDesignId 3, Gas = ItemDesignId 2 (stored as items in the ship)
    const itemQty = (designId: string) => {
      const item = sDoc?.querySelector(`Item[ItemDesignId="${designId}"]`);
      return item?.getAttribute('Quantity') ?? null;
    };

    const rankMap: Record<string, string> = {
      'FleetAdmiral': 'Fleet Admiral', 'ViceAdmiral': 'Vice Admiral',
      'Commander': 'Commander', 'Major': 'Major', 'Member': 'Member',
    };
    const rankId = attr(user, 'AllianceMembership');

    // Build roomId -> roomDesignId map to identify lift rooms
    const roomDesignMap = new Map<string, string>();
    sDoc?.querySelectorAll('Room').forEach(room => {
      const rid = room.getAttribute('RoomId');
      const did = room.getAttribute('RoomDesignId');
      if (rid && did) roomDesignMap.set(rid, did);
    });
    // Build instance-id → {designId, quantity} map from ship items (for gear lookup + inventory)
    const shipItemDetails = new Map<string, { designId: string; quantity: number; bonusEnhancementType?: string; bonusEnhancementValue?: string; bonusEnhancementType2?: string; bonusEnhancementValue2?: string }>();
    sDoc?.querySelectorAll('Item[ItemDesignId]').forEach(item => {
      const instId = item.getAttribute('ItemId');
      const desId  = item.getAttribute('ItemDesignId');
      const qty    = parseInt(item.getAttribute('Quantity') ?? '0', 10);
      const bonusEnhancementType = item.getAttribute('BonusEnhancementType') ?? item.getAttribute('bonusEnhancementType') ?? '';
      const bonusEnhancementValue = item.getAttribute('BonusEnhancementValue') ?? item.getAttribute('bonusEnhancementValue') ?? '';
      const bonusEnhancementType2 = item.getAttribute('BonusEnhancementType2') ?? item.getAttribute('bonusEnhancementType2') ?? '';
      const bonusEnhancementValue2 = item.getAttribute('BonusEnhancementValue2') ?? item.getAttribute('bonusEnhancementValue2') ?? '';
      if (instId && desId) shipItemDetails.set(instId, {
        designId: desId,
        quantity: qty,
        bonusEnhancementType,
        bonusEnhancementValue,
        bonusEnhancementType2,
        bonusEnhancementValue2,
      });
    });
    const shipItemMap = new Map<string, string>(
      Array.from(shipItemDetails.entries()).map(([k, v]) => [k, v.designId])
    );
    // Track which item instances are equipped on which crew member (instance → characterId)
    const instanceEquippedBy = new Map<string, string>();
    // Use ListAllUserDataFirst2 as the authoritative character source when available.
    // It includes stash crew (RoomId=0) that GetShipByUserId omits.
    const charDoc = userDataXml
      ? new DOMParser().parseFromString(userDataXml, 'text/xml')
      : sDoc;
    const crew: PssCrewMember[] = Array.from(charDoc?.querySelectorAll('Character') ?? [])
      .map(c => {
        const roomId = c.getAttribute('RoomId') ?? '0';
        const roomDesignId = roomDesignMap.get(roomId) ?? '';
        const inLift = roomId === '0' || LIFT_DESIGN_IDS.has(roomDesignId);
        const deploymentDate = c.getAttribute('DeploymentDate') ?? '';
        // Donated crew have a real (non-placeholder) DeploymentDate within the 24h donation window.
        // Stash crew that were previously deployed also keep their old DeploymentDate, but those
        // dates are expired (>24h ago), so the expiry check excludes them.
        const isRealDate = deploymentDate !== '' && !deploymentDate.startsWith('2000-01-01') && !deploymentDate.startsWith('1900-01-01');
        const donationExpiry = isRealDate ? new Date(deploymentDate + 'Z').getTime() + 24 * 3600 * 1000 : 0;
        const isActiveDate = isRealDate && donationExpiry > Date.now();
        // Two PSS behaviours for donated crew:
        // 1) RoomId=0 with a non-expired DeploymentDate (donor's stash, date within 24h)
        // 2) Non-zero RoomId that isn't in the player's own roomDesignMap (crew moved to alliance ship room)
        //    PSS doesn't always set DeploymentDate for case 2, so we rely on the room lookup alone.
        const inAllianceRoom = roomId !== '0' && !roomDesignMap.has(roomId);
        const donated = inAllianceRoom || (roomId === '0' && isActiveDate);
        const itemIdsStr = c.getAttribute('ItemIds') ?? '';
        const charId = c.getAttribute('CharacterId') ?? '';
        if (itemIdsStr) {
          itemIdsStr.split(',').filter(Boolean).forEach(id => instanceEquippedBy.set(id.trim(), charId));
        }
        const equipItemIds = itemIdsStr
          ? itemIdsStr.split(',').filter(Boolean).map(id => id.trim())
          : [];
        const equipDesignIds = equipItemIds
          .map(id => shipItemMap.get(id) ?? '')
          .filter(Boolean);
        return {
          characterId:  c.getAttribute('CharacterId') ?? '',
          designId:     c.getAttribute('CharacterDesignId') ?? '',
          customName:   c.getAttribute('CharacterName') ?? '',
          deployed:     !inLift && !donated,
          donated,
          deploymentDate,
          roomId,
          roomDesignId,
          level:        c.getAttribute('Level') ?? '1',
          xp:           c.getAttribute('Xp') ?? '0',
          hpImp:        c.getAttribute('HpImprovement') ?? '0',
          pilotImp:     c.getAttribute('PilotImprovement') ?? '0',
          attackImp:    c.getAttribute('AttackImprovement') ?? '0',
          weaponImp:    c.getAttribute('WeaponImprovement') ?? '0',
          scienceImp:   c.getAttribute('ScienceImprovement') ?? '0',
          engineImp:    c.getAttribute('EngineImprovement') ?? '0',
          abilityImp:   c.getAttribute('AbilityImprovement') ?? '0',
          repairImp:    c.getAttribute('RepairImprovement') ?? '0',
          equipDesignIds,
          equipItemIds,
          maxDonate: Number(c.getAttribute('MaxDonate') ?? '0') || 0,
        };
      });

    // Build inventory from ship items (excluding mineral/gas resources)
    const crewById = new Map<string, PssCrewMember>(crew.map(c => [c.characterId, c]));
    const invMap = new Map<string, { quantity: number; equippedByIds: Set<string>; equippedItemIds: string[]; freeInstances: { id: string; qty: number }[]; instanceBonuses: Array<{ itemId: string; enhancementType?: string; enhancementValue?: string; enhancementType2?: string; enhancementValue2?: string }> }>();
    shipItemDetails.forEach(({ designId, quantity, bonusEnhancementType, bonusEnhancementValue, bonusEnhancementType2, bonusEnhancementValue2 }, instanceId) => {
      if (designId === '2' || designId === '3') return; // skip minerals/gas
      if (quantity <= 0) return; // skip depleted/phantom items
      if (!invMap.has(designId)) invMap.set(designId, { quantity: 0, equippedByIds: new Set(), equippedItemIds: [], freeInstances: [], instanceBonuses: [] });
      const entry = invMap.get(designId)!;
      entry.quantity += quantity;
      const wearer = instanceEquippedBy.get(instanceId);
      if (wearer) {
        entry.equippedByIds.add(wearer);
        entry.equippedItemIds.push(instanceId);
      } else {
        entry.freeInstances.push({ id: instanceId, qty: quantity });
      }
      if (bonusEnhancementType || bonusEnhancementValue || bonusEnhancementType2 || bonusEnhancementValue2) {
        entry.instanceBonuses.push({
          itemId: instanceId,
          enhancementType: bonusEnhancementType,
          enhancementValue: bonusEnhancementValue,
          enhancementType2: bonusEnhancementType2,
          enhancementValue2: bonusEnhancementValue2,
        });
      }
    });
    const inventory: PssInventoryItem[] = Array.from(invMap.entries()).map(([designId, { quantity, equippedByIds, equippedItemIds, freeInstances, instanceBonuses }]) => ({
      designId,
      quantity,
      freeInstances,
      equippedItemIds,
      instanceBonuses: instanceBonuses.length ? instanceBonuses : undefined,
      equippedBy: Array.from(equippedByIds).map(id => crewById.get(id)).filter((c): c is PssCrewMember => !!c),
    }));

    // Parse character AI actions
    const characterActions: PssCharacterAction[] = Array.from(
      charDoc?.querySelectorAll('CharacterAction') ?? []
    ).map(a => ({
      characterActionId: a.getAttribute('CharacterActionId') ?? '',
      characterId:       a.getAttribute('CharacterId') ?? '',
      index:             parseInt(a.getAttribute('CharacterActionIndex') ?? '0', 10),
      conditionTypeId:   a.getAttribute('ConditionTypeId') ?? '',
      actionTypeId:      a.getAttribute('ActionTypeId') ?? '',
    }));

    // Parse rooms (both deployed and inventory)
    const roomElements = Array.from(charDoc?.querySelectorAll('Room') ?? []);
    const rooms: PssRoom[] = roomElements
      .map(r => ({
        roomId:       r.getAttribute('RoomId') ?? '',
        roomDesignId: r.getAttribute('RoomDesignId') ?? '',
        row:          parseInt(r.getAttribute('Row') ?? '0', 10),
        col:          parseInt(r.getAttribute('Column') ?? '0', 10),
        status:       r.getAttribute('RoomStatus') ?? '',
        constructionStartDate: r.getAttribute('ConstructionStartDate') ?? '',
        manufactureStartDate: r.getAttribute('ManufactureStartDate') ?? '',
        upgradeRoomDesignId: r.getAttribute('UpgradeRoomDesignId') ?? '',
      }));

    // Parse room AI actions
    const roomActions: PssRoomAction[] = Array.from(charDoc?.querySelectorAll('RoomAction') ?? [])
      .map(a => ({
        roomActionId:   a.getAttribute('RoomActionId') ?? '',
        roomId:         a.getAttribute('RoomId') ?? '',
        index:          parseInt(a.getAttribute('RoomActionIndex') ?? '0', 10),
        conditionTypeId: a.getAttribute('ConditionTypeId') ?? '',
        actionTypeId:   a.getAttribute('ActionTypeId') ?? '',
      }));

    // Parse battle history from ListAllUserDataFirst2
    const battles: PssBattle[] = userDataXml ? this.parseBattlesFromXml(userDataXml) : [];

    // Parse star system ownership details
    const starSystemDetails: PssStarSystemDetail[] = Array.from(
      charDoc?.querySelectorAll('StarSystemDetail') ?? []
    ).map(s => ({
      starSystemId:              s.getAttribute('StarSystemId') ?? '',
      ownerType:                 s.getAttribute('OwnerType') ?? 'None',
      ownerName:                 s.getAttribute('OwnerName') ?? '',
      ownerId:                   s.getAttribute('OwnerId') ?? '0',
      engagementCooldownEndDate: s.getAttribute('EngagementCooldownEndDate') ?? '',
      immunityEndDate:           s.getAttribute('ImmunityEndDate') ?? '',
      attackerType:              s.getAttribute('AttackerType') ?? 'None',
      attackerName:              s.getAttribute('AttackerName') ?? '',
      attackerId:                s.getAttribute('AttackerId') ?? '0',
    }));

    const nowMs = Date.now();
    const rushTargets: PssRushTarget[] = [];
    const rushSeen = new Set<string>();
    const pushRushTarget = (target: PssRushTarget) => {
      const key = target.targetType + ':' + target.targetId;
      if (rushSeen.has(key)) return;
      rushSeen.add(key);
      rushTargets.push(target);
    };

    const roomEndDateKeys = [
      'UpgradeEndDate', 'ConstructionEndDate', 'ManufactureEndDate',
      'ManufacturingEndDate', 'RepairEndDate', 'QueueEndDate', 'ActionEndDate',
    ];

    roomElements.forEach(r => {
      const roomId = r.getAttribute('RoomId') ?? '';
      if (!roomId) return;
      const roomDesignId = r.getAttribute('RoomDesignId') ?? '';
      const status = r.getAttribute('RoomStatus') ?? '';
      const endDate = pickDateAttr(r, roomEndDateKeys);
      const startDate = pickDateAttr(r, ['ConstructionStartDate', 'ManufactureStartDate', 'UpgradeStartDate', 'ActionStartDate']);
      const endMs = toUtcMs(endDate);
      const hasFutureTimer = endMs > nowMs;
      const statusNorm = status.toLowerCase();
      const statusImpliesBusy = !!statusNorm && statusNorm !== 'normal';
      const hasStartSignal = !!startDate;
      if (!hasFutureTimer && !statusImpliesBusy && !hasStartSignal) return;

      const upgradeRoomDesignId = r.getAttribute('UpgradeRoomDesignId') ?? '';
      const roomLabel = upgradeRoomDesignId && upgradeRoomDesignId !== '0'
        ? ('Room #' + roomDesignId + ' → #' + upgradeRoomDesignId)
        : (roomDesignId ? ('Room #' + roomDesignId) : ('Room #' + roomId));

      pushRushTarget({
        targetType: 'room',
        targetId: roomId,
        label: roomLabel,
        status: status || 'Upgrading',
        endDate,
        startDate,
        designId: roomDesignId || undefined,
        remainingSeconds: hasFutureTimer ? Math.max(0, Math.floor((endMs - nowMs) / 1000)) : null,
      });
    });

    const upgradeShipDesignId = ra(ship, 'UpgradeShipDesignId') ?? '';
    const shipEndDate = pickDateAttr(ship, [
      'UpgradeEndDate', 'ConstructionEndDate', 'ShipUpgradeEndDate', 'ActionEndDate'
    ]);
    const shipEndMs = toUtcMs(shipEndDate);
    if (upgradeShipDesignId && upgradeShipDesignId !== '0') {
      pushRushTarget({
        targetType: 'ship',
        targetId: ra(ship, 'ShipId') ?? 'ship',
        label: 'Ship Upgrade',
        status: 'Upgrading',
        endDate: shipEndDate,
        startDate: pickDateAttr(ship, ['UpgradeStartDate', 'ConstructionStartDate', 'ShipUpgradeStartDate', 'ActionStartDate']),
        designId: upgradeShipDesignId,
        remainingSeconds: shipEndMs > nowMs ? Math.max(0, Math.floor((shipEndMs - nowMs) / 1000)) : null,
      });
    }

    const researchNodes = Array.from(
      charDoc?.querySelectorAll('Research, UserResearch, ResearchTask, Project, LabResearch, LaboratoryResearch') ?? []
    );
    const researchEndDateKeys = ['ResearchEndDate', 'EndDate', 'CompletionDate', 'CompleteDate', 'FinishDate'];
    researchNodes.forEach(node => {
      const status = ra(node, 'Status') ?? ra(node, 'ResearchStatus') ?? ra(node, 'ResearchState') ?? '';
      const statusNorm = status.toLowerCase();
      const endDate = pickDateAttr(node, researchEndDateKeys);
      const startDate = pickDateAttr(node, ['ResearchStartDate', 'StartDate', 'BeginDate']);
      const endMs = toUtcMs(endDate);
      const hasFutureTimer = endMs > nowMs;
      const statusImpliesBusy = !!statusNorm &&
        statusNorm !== 'completed' &&
        !statusNorm.includes('complete') &&
        !statusNorm.includes('finished') &&
        !statusNorm.includes('done') &&
        statusNorm !== 'none';
      if (!hasFutureTimer && !statusImpliesBusy && !startDate) return;
      const rid =
        ra(node, 'ResearchId') ??
        ra(node, 'UserResearchId') ??
        ra(node, 'ResearchDesignId') ??
        ra(node, 'ProjectId') ??
        '';
      const designId = ra(node, 'ResearchDesignId') ?? '';
      const explicitName =
        ra(node, 'ResearchName') ??
        ra(node, 'Name') ??
        ra(node, 'ProjectName') ??
        '';
      const name = explicitName ||
        (designId ? ('Research Design #' + designId) : '') ||
        (rid ? ('Research #' + rid) : 'Research');
      pushRushTarget({
        targetType: 'research',
        targetId: rid || name,
        label: name,
        status: status || 'Researching',
        endDate,
        startDate,
        designId: designId || undefined,
        level: ra(node, 'Level') ?? ra(node, 'ResearchLevel') ?? undefined,
        logoSpriteId: ra(node, 'LogoSpriteId') ?? ra(node, 'ImageSpriteId') ?? ra(node, 'IconSpriteId') ?? undefined,
        remainingSeconds: hasFutureTimer ? Math.max(0, Math.floor((endMs - nowMs) / 1000)) : null,
      });
    });

    if (!researchNodes.length) {
      const fallbackResearchEndDate = pickDateAttr(user, ['ResearchEndDate', 'CurrentResearchEndDate']);
      const fallbackResearchEndMs = toUtcMs(fallbackResearchEndDate);
      if (fallbackResearchEndMs > nowMs) {
        const fallbackResearchName =
          ra(user, 'CurrentResearchName') ??
          ra(user, 'ResearchName') ??
          'Research';
        const fallbackResearchId =
          ra(user, 'CurrentResearchDesignId') ??
          ra(user, 'ResearchDesignId') ??
          fallbackResearchName;
        pushRushTarget({
          targetType: 'research',
          targetId: fallbackResearchId,
          label: fallbackResearchName,
          status: 'Researching',
          endDate: fallbackResearchEndDate,
          startDate: pickDateAttr(user, ['CurrentResearchStartDate', 'ResearchStartDate']),
          designId: ra(user, 'CurrentResearchDesignId') ?? ra(user, 'ResearchDesignId') ?? undefined,
          remainingSeconds: Math.max(0, Math.floor((fallbackResearchEndMs - nowMs) / 1000)),
        });
      }
    }

    rushTargets.sort((a, b) => {
      const sa = a.remainingSeconds ?? Number.MAX_SAFE_INTEGER;
      const sb = b.remainingSeconds ?? Number.MAX_SAFE_INTEGER;
      if (sa !== sb) return sa - sb;
      return a.label.localeCompare(b.label);
    });

    const boostAmountRaw = parseInt(ra(user, 'BoostAmount') ?? ra(promoUser, 'BoostAmount') ?? '0', 10);
    const lastBoostDate = ra(user, 'LastBoostDate') ?? ra(promoUser, 'LastBoostDate') ?? '';
    const boostAmtSeconds = PssAuthService.calculateBoostAmountSeconds(lastBoostDate, nowMs, boostAmountRaw);
    const boostGauge: PssBoostGaugeInfo | null = (boostAmountRaw > 0 || lastBoostDate)
      ? {
        nextAvailableDate: ra(user, 'BoostEndDate') ?? ra(promoUser, 'BoostEndDate') ?? '',
        isReady: boostAmtSeconds >= 300,
        secondsUntilReady: Math.max(0, 300 - boostAmtSeconds),
        boostAmountSeconds: boostAmtSeconds,
        hasBoostAmount: boostAmtSeconds > 0,
        boostAmountHhmm: secondsToHhmm(boostAmtSeconds),
        lastBoostDate,
        source: 'parsed',
      }
      : null;

    // no boost candidate logic is needed here.

    return {
      name:             attr(user, 'Name'),
      shipLevel:        ra(ship, 'ShipLevel') ?? ra(user, 'ShipLevel') ?? '—',
      shipDesignId:     ra(ship, 'ShipDesignId') ?? '',
      trophies:         attr(user, 'Trophy'),
      highestTrophy:    attr(user, 'HighestTrophy'),
      powerScore:       ra(ship, 'PowerScore') ?? '—',
      minerals:         itemQty('3') ?? '—',
      gas:              itemQty('2') ?? '—',
      starbux:          ra(user, 'Credits') ?? ra(user, 'Starbux') ?? '—',  // PSS stores starbux as Credits
      bux:              ra(user, 'Bux') ?? ra(user, 'Starbux') ?? ra(user, 'Credits') ?? '—',
      doves:            ra(user, 'Doves') ?? ra(user, 'Dove') ?? '—',
      globalRanking:    '',
      globalRankTotalUsers: '',
      globalRankTotalActiveUsers: '',
      allianceId:       ra(fleet, 'AllianceId') ?? ra(fleet, 'Id') ?? ra(user, 'AllianceId') ?? ra(charDoc?.querySelector('User') ?? null, 'AllianceId') ?? '',
      fleetName:        attr(fleet, 'AllianceName') || attr(user, 'AllianceName'),
      fleetMembership:  rankMap[rankId] ?? (rankId !== '—' ? rankId : 'None'),
      lastLoginDate:    attr(user, 'LastLoginDate'),
      creationDate:     attr(user, 'CreationDate'),
      pvpAttackWins:    attr(user, 'PVPAttackWins'),
      pvpAttackLosses:  attr(user, 'PVPAttackLosses'),
      pvpDefenceWins:   attr(user, 'PVPDefenceWins'),
      pvpDefenceLosses: attr(user, 'PVPDefenceLosses'),
      hasSubscription:  (() => { const v = ra(user, 'VipExpiryDate'); return !!v && !v.startsWith('2000-01-01') && !v.startsWith('1900-01-01') && new Date(v + 'Z') > new Date(); })(),
      frame:            ra(user, 'Frame') ?? ra(user, 'UserFrame') ?? undefined,
      pin:              ra(user, 'Pin') ?? ra(user, 'UserPin') ?? undefined,
      ribbon:           ra(user, 'Ribbon') ?? ra(user, 'UserRibbon') ?? undefined,
      crew,
      characterActions,
      rooms,
      roomActions,
      starSystemDetails,
      battles,
      inventory,
      boostGauge,
      rushTargets,
    };
  }

  private parseBattlesFromXml(xml: string): PssBattle[] {
    const doc = new DOMParser().parseFromString(xml, 'text/xml');
    const results: PssBattle[] = [];

    const parseRewardInt = (rewards: string, key: string): number => {
      const m = new RegExp(`(?:^|\\|)${key}:(\\d+)`).exec(rewards);
      return m ? parseInt(m[1], 10) : 0;
    };

    // Both <Battle> (from ListAllUserDataFirst2) and <UserBattle> (from BattleService) elements
    doc.querySelectorAll('Battle, UserBattle').forEach(el => {
      const g = (name: string) => el.getAttribute(name) ?? '';
      const battleId = g('BattleId') || g('UserBattleId');
      if (!battleId) return;

      const attackingUserId = g('AttackingUserId') || g('AttackerId') || g('UserId');
      const defendingUserId = g('DefendingUserId') || g('DefenderId') || g('TargetId');
      if (!attackingUserId || !defendingUserId) return;

      const outcomeType = g('OutcomeType');
      // Determine the relevant reward string: if attacker won use AttackerWinRewards, else AttackerLoseRewards, etc.
      const attackerWinRewards  = g('AttackerWinRewards')  || g('WinRewards');
      const attackerLoseRewards = g('AttackerLoseRewards') || g('LoseRewards');
      const defenderWinRewards  = g('DefenderWinRewards');
      const defenderLoseRewards = g('DefenderLoseRewards');

      // Pick the reward string for the "winning" side to parse star/resource values
      const winnerRewards = outcomeType === 'Attacker Won' ? attackerWinRewards : defenderWinRewards;

      results.push({
        battleId,
        attackingUserId,
        defendingUserId,
        attackingShipName:    g('AttackingShipName') || g('AttackerName') || g('UserName'),
        defendingShipName:    g('DefendingShipName') || g('DefenderName') || g('TargetName'),
        attackingAllianceName: g('AttackingAllianceName'),
        defendingAllianceName: g('DefendingAllianceName'),
        outcomeType,
        winTrophyResult:  parseInt(g('WinTrophyResult')  || '0', 10),
        loseTrophyResult: parseInt(g('LoseTrophyResult') || '0', 10),
        battleType:    g('BattleType'),
        isStarBattle:  g('IsStarBattle').toLowerCase() === 'true',
        battleDate:    g('BattleDate') || g('TargetDate') || g('DateTime'),
        battleEndDate: g('BattleEndDate'),
        leagueType:    g('LeagueType'),
        attackerWinRewards,
        attackerLoseRewards,
        defenderWinRewards,
        defenderLoseRewards,
        starsWon:    parseRewardInt(winnerRewards, 'score'),
        xpWon:       parseRewardInt(winnerRewards, 'xp'),
        mineralsWon: parseRewardInt(winnerRewards, 'mineral'),
        gasWon:      parseRewardInt(winnerRewards, 'gas'),
      });
    });

    // Sort newest first
    results.sort((a, b) => b.battleDate.localeCompare(a.battleDate));
    return results;
  }

  // ── Crew actions ──────────────────────────────────────────────────────────

  donateCrew(characterId: string): Observable<{ xml: string }> {
    return this.ensureFreshSession().pipe(
      switchMap(s => this.http.post<{ xml: string }>('/api/crew/donate', {
        characterId,
        accessToken: s.accessToken,
        server: s.productionServer,
        userId: s.userId,
      }))
    );
  }

  extendDonation(characterId: string): Observable<{ xml: string }> {
    return this.ensureFreshSession().pipe(
      switchMap(s => {
        return this.http.post<{ xml: string }>('/api/crew/extend', {
          characterId,
          accessToken: s.accessToken,
          server: s.productionServer,
          userId: s.userId,
        });
      })
    );
  }

  getCrewDonationHistory(userId: string): Observable<PssDonationHistoryItem[]> {
    if (!userId) return throwError(() => new Error('userId is required'));
    return this.http.get<{ donations: PssDonationHistoryItem[] }>(`/api/crew/donation-history?userId=${encodeURIComponent(userId)}`).pipe(
      map(resp => resp.donations || []),
      catchError(() => of([]))
    );
  }

  setCrewDonationHistory(
    userId: string,
    characterId: string,
    deploymentDate: string,
    characterName?: string | null,
    userName?: string | null,
    characterCustomName?: string | null,
  ): Observable<{ success: boolean }> {
    if (!userId || !characterId || !deploymentDate) return throwError(() => new Error('userId, characterId, and deploymentDate are required'));
    return this.http.post<{ success: boolean }>('/api/crew/donation-history', {
      userId,
      characterId,
      deploymentDate,
      characterName: characterName ?? null,
      characterCustomName: characterCustomName ?? null,
      userName: userName ?? null,
    }).pipe(
      catchError(() => of({ success: false }))
    );
  }

  // ── User polling task automation helpers ───────────────────────────────────

  listPollingTasks(): Observable<PssPollingTask[]> {
    return this.http.get<{ tasks: PssPollingTask[] }>('/api/polling-tasks').pipe(
      map(resp => resp.tasks || []),
      catchError(() => of([]))
    );
  }

  createPollingTask(task: {
    taskType: string;
    targetId?: string | null;
    description?: string;
    scheduleType?: 'interval' | 'cron' | 'times';
    intervalSec?: number;
    cronExpression?: string | null;
    scheduleTimes?: string[];
  }): Observable<PssPollingTask> {
    return this.http.post<{ task: PssPollingTask }>('/api/polling-tasks', task).pipe(
      map(resp => resp.task)
    );
  }

  updatePollingTask(taskId: number, updates: {
    description?: string;
    targetId?: string | null;
    scheduleType?: 'interval' | 'cron' | 'times';
    intervalSec?: number;
    cronExpression?: string | null;
    scheduleTimes?: string[];
    isActive?: boolean;
  }): Observable<PssPollingTask> {
    return this.http.patch<{ task: PssPollingTask }>(`/api/polling-tasks/${taskId}`, updates).pipe(
      map(resp => resp.task)
    );
  }

  deletePollingTask(taskId: number): Observable<{ deleted: number }> {
    return this.http.delete<{ deleted: number }>(`/api/polling-tasks/${taskId}`);
  }

  executePollingTasks(): Observable<{ due: number; executed: number }> {
    return this.http.post<{ due: number; executed: number }>('/api/polling-tasks/execute', {}).pipe(
      catchError(() => of({ due: 0, executed: 0 }))
    );
  }

  // ── Item activation ───────────────────────────────────────────────────────

  private parseXmlTagAttributes(xml: string, tag: string): Record<string, string> {
    const result: Record<string, string> = {};
    const tagMatch = xml.match(new RegExp(`<${tag}\\b([^>]*)>`, 'i'));
    if (!tagMatch || !tagMatch[1]) return result;
    const attrPart = tagMatch[1];
    const attrRe = /(\w+)="([^"]*)"/g;
    let m: RegExpExecArray | null;
    while (m = attrRe.exec(attrPart)) {
      result[m[1]] = m[2];
    }
    return result;
  }

  activateItem(itemId: string): Observable<{ xml: string; rewardDesignId: string; rewardQty: number; rewardNewTotal: number | null; character?: PssCrewMember }> {
    if (!itemId) return throwError(() => new Error('Item ID is missing — please close and reopen the item'));

    return this.ensureFreshSession().pipe(
      switchMap(s => {
    const server = s.productionServer || 'api.pixelstarships.com';
    const activate$ = this.http.post<{ xml: string }>('/api/item/activate', {
      itemId,
      accessToken: s.accessToken,
      server,
    });

    return activate$.pipe(
      timeout({ each: 65000, with: () => throwError(() => new Error('Request timed out — the PSS server is not responding')) }),
      map(resp => {
        // result="item:744x1" → rewardDesignId=744, rewardQty=1
        const resultMatch = resp.xml?.match(/result="item:(\d+)x(\d+)"/);
        const rewardDesignId = resultMatch?.[1] ?? '';
        const rewardQty = parseInt(resultMatch?.[2] ?? '1', 10);
        // <Item ItemDesignId="744" Quantity="81" ...> → new total on ship
        const itemMatch = resp.xml?.match(/<Item\b[^>]*\bItemDesignId="(\d+)"[^>]*\bQuantity="(\d+)"/);
        const rewardNewTotal = itemMatch ? parseInt(itemMatch[2], 10) : null;

        const characterAttrs = this.parseXmlTagAttributes(resp.xml ?? '', 'Character');
        let character: PssCrewMember | undefined;
        if (characterAttrs['CharacterId']) {
          const charDesignId = characterAttrs['CharacterDesignId'] ?? '0';
          const roomId = characterAttrs['RoomId'] ?? '0';
          character = {
            characterId: characterAttrs['CharacterId'],
            designId: charDesignId,
            customName: characterAttrs['CharacterName'] ?? '',
            deployed: roomId !== '0',
            donated: false,
            deploymentDate: characterAttrs['DeploymentDate'] ?? '',
            roomId,
            roomDesignId: characterAttrs['RoomDesignId'] ?? '',
            level: characterAttrs['Level'] ?? '0',
            xp: characterAttrs['Xp'] ?? '0',
            hpImp: characterAttrs['HpImprovement'] ?? '0',
            pilotImp: characterAttrs['PilotImprovement'] ?? '0',
            attackImp: characterAttrs['AttackImprovement'] ?? '0',
            weaponImp: characterAttrs['WeaponImprovement'] ?? '0',
            scienceImp: characterAttrs['ScienceImprovement'] ?? '0',
            engineImp: characterAttrs['EngineImprovement'] ?? '0',
            abilityImp: characterAttrs['AbilityImprovement'] ?? '0',
            repairImp: characterAttrs['RepairImprovement'] ?? '0',
            stamina: characterAttrs['Stamina'] ?? '0',
            fatigue: characterAttrs['Fatigue'] ?? '0',
            walk: characterAttrs['Walk'] ?? '0',
            run: characterAttrs['Run'] ?? '0',
            equipDesignIds: [],
            equipItemIds: [],
            isNew: (characterAttrs['IsNew'] ?? '').toLowerCase() === 'true',
          };
        }

        return { xml: resp.xml, rewardDesignId, rewardQty, rewardNewTotal, character };
      })
    );
      })
    );
  }

  /** Permanently deletes (fires) a crew member via CharacterService/DeleteCharacter. */
  deleteCharacter(characterId: string): Observable<{ xml: string }> {
    if (!characterId) return throwError(() => new Error('Character ID is missing'));

    return this.ensureFreshSession().pipe(
      switchMap(s => this.http.post<{ xml: string }>('/api/character/delete', {
        characterId,
        accessToken: s.accessToken,
        server: s.productionServer || 'api.pixelstarships.com',
      })),
      catchError(err => {
        const message = err?.error?.error || err?.message || 'Failed to delete character';
        return throwError(() => new Error(message));
      })
    );
  }

  // ── Boost gauge / rush (research-only)

  probeBoostEndpoints(targetId = '0'): Observable<{
    hints: string[];
    results: Array<{ targetType: string; endpoint: string; exists: boolean; error?: string }>;
  }> {
    return throwError(() => new Error('Boost probe is not supported in this branch'));
  }

  useBoost(targetType: 'room' | 'ship' | 'research', targetId: string): Observable<{ xml: string; endpoint: string }> {
    if (!targetId) return throwError(() => new Error('targetId is required for boost'));
    const clientDateTime = new Date().toISOString().slice(0, 19);
    return this.ensureFreshSession().pipe(
      switchMap(s => this.http.post<{ xml: string }>('/api/boost/use', {
        targetType,
        targetId,
        accessToken: s.accessToken,
        server: s.productionServer,
        clientDateTime,
      })),
      map(resp => {
        const endpoint =
          targetType === 'room' ? 'RoomService/SpeedUpRoomConstructionUsingBoostGauge' :
          targetType === 'ship' ? 'ShipService/SpeedUpShipConstructionUsingBoostGauge' :
          'ResearchService/SpeedUpResearchUsingBoostGauge';
        return { xml: resp.xml, endpoint };
      }),
      catchError(err => {
        const message = err?.error?.error || err?.message || 'Failed to use boost';
        return throwError(() => new Error(message));
      })
    );
  }

  // ── Ship XML fetch (used to verify AI wipe before re-adding rules) ─────────

  /** Returns raw GetShipByUserId XML for the current session's user. */
  getShipXml(): Observable<string> {
    return this.ensureFreshSession().pipe(
      switchMap(s => {
        const token = encodeURIComponent(s.accessToken);
        const userId = s.userId;
        const server = s.productionServer;
        const clientDateTime = encodeURIComponent(new Date().toISOString().slice(0, 19));
        return this.http.get(
          `/pss/${server}/ShipService/GetShipByUserId?userId=${userId}&accessToken=${token}&clientDateTime=${clientDateTime}&checksum=0`,
          { responseType: 'text' }
        );
      })
    );
  }

  /**
   * Queue AI mutations so create/delete/move calls never overlap.
   * The game API is order-sensitive, so operations must execute strictly one-by-one.
   */
  private queueAiMutation<T>(factory: () => Observable<T>): Observable<T> {
    return new Observable<T>(subscriber => {
      let canceled = false;

      this.aiMutationChain = this.aiMutationChain
        .catch(() => undefined)
        .then(() => new Promise<void>((resolve) => {
          if (canceled) {
            resolve();
            return;
          }

          factory().subscribe({
            next: value => subscriber.next(value),
            error: err => {
              subscriber.error(err);
              resolve();
            },
            complete: () => {
              subscriber.complete();
              resolve();
            },
          });
        }));

      return () => {
        canceled = true;
      };
    });
  }

  // ── Crew AI actions ───────────────────────────────────────────────────────

  createCharacterAction(characterId: string, conditionTypeId: string, actionTypeId: string, index: number): Observable<{ xml: string }> {
    return this.queueAiMutation(() =>
      this.ensureFreshSession().pipe(
        switchMap(s => this.http.post<{ xml: string }>('/api/ai/character/create', {
          characterId, conditionTypeId, actionTypeId, index,
          accessToken: s.accessToken,
          server: s.productionServer,
        }))
      )
    );
  }

  editCharacterAction(characterActionId: string, conditionTypeId: string, actionTypeId: string): Observable<{ xml: string }> {
    return this.queueAiMutation(() =>
      this.ensureFreshSession().pipe(
        switchMap(s => this.http.post<{ xml: string }>('/api/ai/character/edit', {
          characterActionId, conditionTypeId, actionTypeId,
          accessToken: s.accessToken,
          server: s.productionServer,
        }))
      )
    );
  }

  deleteCharacterAction(characterActionId: string): Observable<{ xml: string }> {
    return this.queueAiMutation(() =>
      this.ensureFreshSession().pipe(
        switchMap(s => this.http.post<{ xml: string }>('/api/ai/character/delete', {
          characterActionId,
          accessToken: s.accessToken,
          server: s.productionServer,
        }))
      )
    );
  }

  deleteAllCharacterActions(characterId: string): Observable<{ xml: string }> {
    return this.queueAiMutation(() =>
      this.ensureFreshSession().pipe(
        switchMap(s => this.http.post<{ xml: string }>('/api/ai/character/delete-all', {
          characterId,
          accessToken: s.accessToken,
          server: s.productionServer,
        }))
      )
    );
  }

  createRoomAction(roomId: string, conditionTypeId: string, actionTypeId: string, index: number): Observable<{ xml: string }> {
    return this.queueAiMutation(() =>
      this.ensureFreshSession().pipe(
        switchMap(s => this.http.post<{ xml: string }>('/api/ai/room/create', {
          roomId, conditionTypeId, actionTypeId, index,
          accessToken: s.accessToken,
          server: s.productionServer,
        }))
      )
    );
  }

  editRoomAction(roomActionId: string, conditionTypeId: string, actionTypeId: string): Observable<{ xml: string }> {
    return this.queueAiMutation(() =>
      this.ensureFreshSession().pipe(
        switchMap(s => this.http.post<{ xml: string }>('/api/ai/room/edit', {
          roomActionId, conditionTypeId, actionTypeId,
          accessToken: s.accessToken,
          server: s.productionServer,
        }))
      )
    );
  }

  deleteRoomAction(roomActionId: string): Observable<{ xml: string }> {
    return this.queueAiMutation(() =>
      this.ensureFreshSession().pipe(
        switchMap(s => this.http.post<{ xml: string }>('/api/ai/room/delete', {
          roomActionId,
          accessToken: s.accessToken,
          server: s.productionServer,
        }))
      )
    );
  }

  deleteAllRoomActions(roomId: string): Observable<{ xml: string }> {
    return this.queueAiMutation(() =>
      this.ensureFreshSession().pipe(
        switchMap(s => this.http.post<{ xml: string }>('/api/ai/room/delete-all', {
          roomId,
          accessToken: s.accessToken,
          server: s.productionServer,
        }))
      )
    );
  }

  moveCharacterAction(characterActionId: string, newIndex: number): Observable<{ xml: string }> {
    return this.queueAiMutation(() =>
      this.ensureFreshSession().pipe(
        switchMap(s => this.http.post<{ xml: string }>('/api/ai/character/move', {
          characterActionId, newIndex,
          accessToken: s.accessToken,
          server: s.productionServer,
        }))
      )
    );
  }

  moveRoomAction(roomActionId: string, newIndex: number): Observable<{ xml: string }> {
    return this.queueAiMutation(() =>
      this.ensureFreshSession().pipe(
        switchMap(s => this.http.post<{ xml: string }>('/api/ai/room/move', {
          roomActionId, newIndex,
          accessToken: s.accessToken,
          server: s.productionServer,
        }))
      )
    );
  }

  // ── Fleet info ────────────────────────────────────────────────────────────

  getFleetInfo(server: string, allianceId: string, accessToken: string): Observable<PssFleetInfo> {
    const token = encodeURIComponent(accessToken);
    // ListUsers returns members nested inside an Alliance element:
    // <ListUsers><Alliance AllianceId="..." ...><User Id="..." .../></Alliance></ListUsers>
    const members$ = this.http.get(
      `/pss/${server}/AllianceService/ListUsers?accessToken=${token}&skip=0&take=100&allianceId=${allianceId}`,
      { responseType: 'text' }
    ).pipe(catchError(err => { console.error('[Fleet] ListUsers error', err); return of(''); }));
    const alliance$ = this.http.get(
      `/pss/${server}/AllianceService/GetAlliance?accessToken=${token}&allianceId=${allianceId}`,
      { responseType: 'text' }
    ).pipe(catchError(err => { console.error('[Fleet] GetAlliance error', err); return of(''); }));
    return forkJoin({ membersXml: members$, allianceXml: alliance$ }).pipe(
      map(({ membersXml, allianceXml }) => {

        const mDoc = membersXml ? new DOMParser().parseFromString(membersXml, 'text/xml') : null;

        // The Alliance element inside ListUsers response carries the fleet header fields.
        // Fallback to GetAlliance response if needed.
        const mAlliance  = mDoc?.querySelector('Alliance') ?? null;
        const aDoc = allianceXml ? new DOMParser().parseFromString(allianceXml, 'text/xml') : null;
        const aAlliance  = aDoc?.querySelector('Alliance') ?? null;
        const fleetEl = mAlliance ?? aAlliance;

        const userEls = Array.from(mDoc?.querySelectorAll('User') ?? []);

        const members: PssFleetMember[] = userEls.map(u => ({
          userId:                u.getAttribute('Id') ?? '',
          name:                  u.getAttribute('Name') ?? u.getAttribute('UserName') ?? '',
          trophies:              u.getAttribute('Trophy') ?? '0',
          highestTrophy:         u.getAttribute('HighestTrophy') ?? '0',
          allianceScore:         u.getAttribute('AllianceScore') ?? '0',
          tournamentBonusScore:  u.getAttribute('TournamentBonusScore') ?? '0',
          allianceMembership:    u.getAttribute('AllianceMembership') ?? 'Member',
          crewDonated:           u.getAttribute('CrewDonated') ?? '0',
          crewReceived:          u.getAttribute('CrewReceived') ?? '0',
          lastLoginDate:         u.getAttribute('LastLoginDate') ?? '',
        }));
        return {
          allianceId,
          allianceName:     fleetEl?.getAttribute('AllianceName') ?? '',
          trophy:           fleetEl?.getAttribute('Trophy') ?? '0',
          divisionDesignId: fleetEl?.getAttribute('DivisionDesignId') ?? '0',
          ranking:          fleetEl?.getAttribute('Ranking') ?? '0',
          memberCount:      fleetEl?.getAttribute('AllianceMemberCount') ?? String(members.length),
          members,
        };
      })
    );
  }

  // ── Fleet chat ────────────────────────────────────────────────────────────

  // Stored history written by the fleet_chat polling task. Returned oldest-first.
  // Auth: backend requires X-User-Id and that the caller is in this alliance
  // (or is_admin). Returns empty array if the alliance isn't being polled yet
  // so the caller can transparently fall back to the live PSS API.
  getStoredFleetMessages(allianceId: string, limit = 500): Observable<PssFleetMessage[]> {
    const userId = this.currentSession?.userId;
    if (!userId || !allianceId) return of([] as PssFleetMessage[]);
    return this.http.get<{ messages: PssFleetMessage[] }>(
      `/api/fleet-chat/messages?allianceId=${encodeURIComponent(allianceId)}&limit=${limit}`,
      { headers: { 'X-User-Id': userId } }
    ).pipe(
      map(r => r.messages ?? []),
      catchError(() => of([] as PssFleetMessage[]))
    );
  }

  getFleetMessages(server: string, allianceId: string, accessToken: string): Observable<PssFleetMessage[]> {
    const token = encodeURIComponent(accessToken);
    return this.http.get(
      `/pss/${server}/MessageService/ListMessagesForChannelKey?channelKey=alliance-${allianceId}&accessToken=${token}`,
      { responseType: 'text' }
    ).pipe(
      map(xml => {
        const doc = new DOMParser().parseFromString(xml, 'text/xml');
        return Array.from(doc.querySelectorAll('Message')).map(el => ({
          messageId: el.getAttribute('MessageId') ?? '',
          userId:    el.getAttribute('UserId') ?? '',
          userName:  el.getAttribute('UserName') ?? el.getAttribute('Name') ?? '',
          message:   el.getAttribute('Message') ?? el.textContent?.trim() ?? '',
          dateTime:  el.getAttribute('DateTime') ?? '',
          frame:     el.getAttribute('UserFrame') ?? el.getAttribute('Frame') ?? '',
          pin:       el.getAttribute('UserPin') ?? el.getAttribute('Pin') ?? '',
          ribbon:    el.getAttribute('UserRibbon') ?? el.getAttribute('Ribbon') ?? '',
        }));
      }),
      catchError(() => of([] as PssFleetMessage[]))
    );
  }

  sendFleetMessage(allianceId: string, message: string): Observable<{ xml: string }> {
    return this.ensureFreshSession().pipe(
      switchMap(s => this.http.post<{ xml: string }>('/api/fleet/message', {
        allianceId,
        message,
        accessToken: s.accessToken,
        server: s.productionServer,
      }))
    );
  }

  sendFleetAnnouncement(allianceId: string, message: string): Observable<{ xml: string }> {
    return this.ensureFreshSession().pipe(
      switchMap(s => this.http.post<{ xml: string }>('/api/fleet/announcement', {
        allianceId,
        message,
        accessToken: s.accessToken,
        server: s.productionServer,
      }))
    );
  }

  // ── Member management ─────────────────────────────────────────────────────

  setMembership(userId: string, allianceMembership: string): Observable<{ xml: string }> {
    return this.ensureFreshSession().pipe(
      switchMap(s => this.http.post<{ xml: string }>('/api/fleet/membership', {
        userId,
        allianceMembership,
        accessToken: s.accessToken,
        server: s.productionServer,
      }))
    );
  }

  // ── Friends & private messages ────────────────────────────────────────────

  listFriends(): Observable<{ confirmed: PssFriend[]; pending: PssFriend[] }> {
    return this.ensureFreshSession().pipe(
      switchMap(s => this.http.get<{ xml: string }>('/api/friends/list', {
        params: { userId: s.userId, accessToken: s.accessToken, server: s.productionServer }
      })),
      map(r => {
      const confirmed: PssFriend[] = [];
      const pending:   PssFriend[] = [];
      try {
        const doc = new DOMParser().parseFromString(r.xml, 'text/xml');
        doc.querySelectorAll('Friend').forEach(el => {
          const userId = el.getAttribute('FriendUserId') ?? '';
          if (!userId) return;
          const friend: PssFriend = {
            userId,
            name:            el.getAttribute('Name') ?? userId,
            trophies:        el.getAttribute('FriendTrophy') ?? '0',
            unreadMessages:  parseInt(el.getAttribute('UnreadMessages') ?? '0', 10),
            lastInteraction: el.getAttribute('LastInteractionDate') ?? '',
            frame:           el.getAttribute('FriendFrame') ?? el.getAttribute('Frame') ?? '',
            pin:             el.getAttribute('FriendPin') ?? el.getAttribute('Pin') ?? '',
            ribbon:          el.getAttribute('FriendRibbon') ?? el.getAttribute('Ribbon') ?? '',
          };
          if (el.getAttribute('FriendType') === 'Friend') confirmed.push(friend);
          else pending.push(friend);
        });
      } catch { /* ignore parse errors */ }
      return { confirmed, pending };
    }));
  }

  addFriend(friendUserId: string): Observable<{ xml: string }> {
    return this.ensureFreshSession().pipe(
      switchMap(s => this.http.post<{ xml: string }>('/api/friend/add', {
        friendUserId,
        accessToken: s.accessToken,
        server: s.productionServer,
      }))
    );
  }

  sendPrivateMessage(toUserId: string, message: string): Observable<{ xml: string }> {
    return this.ensureFreshSession().pipe(
      switchMap(s => this.http.post<{ xml: string }>('/api/message/private', {
        toUserId,
        message,
        accessToken: s.accessToken,
        server: s.productionServer,
      }))
    );
  }

  // Fetch all private messages — GET /MessageService/ListPrivateMessages?accessToken=...
  getAllPrivateMessages(): Observable<PssPrivateMessage[]> {
    return this.ensureFreshSession().pipe(
      switchMap(s => {
    const myId = s.userId;
    const token = encodeURIComponent(s.accessToken);
    const url = `/pss/${s.productionServer}/MessageService/ListPrivateMessages?accessToken=${token}`;
    return this.http.get(url, { responseType: 'text' }).pipe(
      map(xml => {
        const doc = new DOMParser().parseFromString(xml, 'text/xml');
        const els = Array.from(doc.querySelectorAll('Message, PrivateMessage'));

        return els.flatMap(el => {
          const msgText = el.getAttribute('Message') ?? el.textContent?.trim() ?? '';
          if (!msgText) return [];

          // PSS format: UserId=sender, ToUserId=recipient, UserName=sender name
          const origSender   = el.getAttribute('UserId') ?? el.getAttribute('FromUserId') ?? '';
          const origRecip    = el.getAttribute('ToUserId') ?? el.getAttribute('RecipientUserId') ?? '';
          const origSenderName = el.getAttribute('UserName') ?? el.getAttribute('Name') ?? el.getAttribute('FromName') ?? '';
          // MessageDate is the actual date field (DateTime is absent)
          const dateStr    = el.getAttribute('MessageDate') ?? el.getAttribute('DateTime') ?? el.getAttribute('SentDate') ?? '';
          const msgId      = el.getAttribute('MessageId') ?? el.getAttribute('PrivateMessageId') ?? '';

          // Some friend-invite messages encode the target friend info inside ActivityArgument
          // e.g. ActivityArgument="&lt;Friend FriendUserId=... Name=... /&gt;". Try to parse that to recover ids/names.
          let activityArg = el.getAttribute('ActivityArgument') ?? el.getAttribute('Argument') ?? '';
          const activityType = el.getAttribute('ActivityType') ?? el.getAttribute('Activity') ?? '';
          let actFriendId = '';
          let actFriendName = '';
          if (activityArg) {
            try {
              // Decode common HTML/XML entities if the argument was escaped
              const decoded = activityArg.replace(/&lt;/g, '<').replace(/&gt;/g, '>').replace(/&quot;/g, '"').replace(/&#39;/g, "'").replace(/&amp;/g, '&');
              const argDoc = new DOMParser().parseFromString(decoded, 'text/xml');
              const f = argDoc.querySelector('Friend');
              if (f) {
                actFriendId = f.getAttribute('FriendUserId') ?? f.getAttribute('FriendId') ?? f.getAttribute('Id') ?? '';
                actFriendName = f.getAttribute('Name') ?? f.getAttribute('FriendName') ?? '';
              }
            } catch { /* ignore parse errors */ }
          }

          const sentByMe = (origSender === myId);
          // Prefer the explicit sender/recipient ids, but fall back to parsed ActivityArgument friend id
          let friendId = sentByMe ? (origRecip || actFriendId) : (origSender || actFriendId);
          if (!friendId && actFriendId) {
            friendId = actFriendId;
          }
          // Prevent self-threads when invite messages can include your own userId in the channel sender field.
          if (friendId === myId && actFriendId && actFriendId !== myId) {
            friendId = actFriendId;
          }

          if (friendId === myId) {
            if (sentByMe && origRecip && origRecip !== myId) {
              friendId = origRecip;
            } else if (!sentByMe && origSender && origSender !== myId) {
              friendId = origSender;
            }
          }

          if (!friendId || friendId === myId) {
            // We don't thread messages to ourselves and can't derive external partner ID.
            return [];
          }

          const fromName = origSenderName || actFriendName || friendId;
          const isInvite = ((activityType ?? '').toLowerCase() === 'invited') || !!actFriendId || /friend request/i.test(msgText);

          if (isInvite) {
            console.debug('[DM] invite message', { origSender, origRecip, friendId, actFriendId, fromName, sentByMe, isInvite, msgText });
          }

          return [{
            messageId:  msgId,
            fromUserId: friendId,
            fromName:   fromName,
            message:    msgText,
            sentDate:   dateStr,
            sentByMe,
            activityType: activityType || undefined,
            isInvite: isInvite || undefined,
            frame:     el.getAttribute('UserFrame') ?? el.getAttribute('Frame') ?? '',
            pin:       el.getAttribute('UserPin') ?? el.getAttribute('Pin') ?? '',
            ribbon:    el.getAttribute('UserRibbon') ?? el.getAttribute('Ribbon') ?? '',
          }];
        });
      }),
      catchError(err => {
        console.error('[DM] error:', err);
        return of([] as PssPrivateMessage[]);
      })
    );
      }),
      catchError(() => of([] as PssPrivateMessage[]))
    );
  }

  // Keep old inbox method as fallback (uses ListPrivateMessagesPaged2 POST)
  getPrivateMessageInbox(skip = 0): Observable<PssPrivateMessage[]> {
    return this.ensureFreshSession().pipe(
      switchMap(s => this.http.post<{ xml: string }>('/api/message/inbox', {
        accessToken: s.accessToken,
        server: s.productionServer,
        skip,
      })),
      map(r => {
        // minimal parse for inbox fallback — sentByMe unknown here
        const doc = new DOMParser().parseFromString(r.xml ?? '', 'text/xml');
        return Array.from(doc.querySelectorAll('Message, PrivateMessage')).flatMap(el => {
          const msgText = el.getAttribute('Message') ?? '';
          if (!msgText) return [];
          return [{
            messageId:  el.getAttribute('MessageId') ?? '',
            fromUserId: el.getAttribute('UserId') ?? el.getAttribute('FromUserId') ?? '',
            fromName:   el.getAttribute('UserName') ?? el.getAttribute('FromName') ?? '',
            message:    msgText,
            sentDate:   el.getAttribute('DateTime') ?? '',
            sentByMe:   false,
          }];
        });
      })
    );
  }

  updateAccountExtras(userId: string, trophies: string, allianceId: string, fleetName: string, shipLevel?: string): void {
    try {
      const raw = localStorage.getItem('pss_accounts');
      const accounts: PssAccount[] = raw ? JSON.parse(raw) : [];
      const idx = accounts.findIndex(a => a.userId === userId);
      if (idx >= 0) {
        accounts[idx].trophies   = trophies;
        accounts[idx].allianceId = allianceId;
        accounts[idx].fleetName  = fleetName;
        accounts[idx].shipLevel  = shipLevel ?? this.currentSession?.shipLevel ?? accounts[idx].shipLevel;
        localStorage.setItem('pss_accounts', JSON.stringify(accounts));
      }
    } catch {}

    // Ensure backend accounts table is synced with latest player details.
    this.http.post('/api/accounts/login', {
      userId,
      userName: this.currentSession?.name ?? '',
      allianceId,
      fleetName,
      shipLevel: this.currentSession?.shipLevel ?? '',
      trophies,
    }).pipe(catchError(() => of(null))).subscribe();
  }

  // ── Battle history ────────────────────────────────────────────────────────

  syncBattles(session: PssSession): Observable<{ battles: PssBattle[]; inserted: number; skipped: number }> {
    return this.http.post<{ battles: PssBattle[]; inserted: number; skipped: number; rawXml?: string }>('/api/battles/sync', {
      userId:      session.userId,
      accessToken: session.accessToken,
      server:      session.productionServer,
    }).pipe(
      catchError(err => { console.error('[battles] sync error:', err); return of({ battles: [], inserted: 0, skipped: 0 }); })
    );
  }

  getBattles(userId: string, limit = 1000): Observable<PssBattle[]> {
    return this.http.get<{ battles: PssBattle[] }>(`/api/battles?userId=${userId}&limit=${limit}`)
      .pipe(
        map(r => r.battles ?? []),
        catchError(() => of([] as PssBattle[]))
      );
  }

  // ── Persist battles to D1 (Cloudflare) ───────────────────────────────────
  // Fire-and-forget: saves battles parsed from userDataXml into the DB so
  // historical data accumulates beyond what the current session XML contains.
  // ── AI Snapshots ─────────────────────────────────────────────────────────

  getAiSnapshots(targetType: 'crew' | 'room', targetId: string): Observable<AiSnapshot[]> {
    const s = this.currentSession;
    if (!s) return of([] as AiSnapshot[]);
    const params = `userId=${encodeURIComponent(s.userId)}&targetType=${targetType}&targetId=${encodeURIComponent(targetId)}`;
    return this.http.get<{ snapshots: AiSnapshot[] }>(`/api/ai/snapshots?${params}`).pipe(
      map(r => r.snapshots ?? []),
      catchError(() => of([] as AiSnapshot[]))
    );
  }

  saveAiSnapshot(targetType: 'crew' | 'room', targetId: string, name: string, rules: { conditionTypeId: string; actionTypeId: string }[]): Observable<AiSnapshot> {
    return this.ensureFreshSession().pipe(
      switchMap(s => this.http.post<AiSnapshot>('/api/ai/snapshots', { userId: s.userId, targetType, targetId, name, rules }))
    );
  }

  deleteAiSnapshot(snapshotId: string): Observable<void> {
    return this.ensureFreshSession().pipe(
      switchMap(s => this.http.post<void>('/api/ai/snapshots/delete', { snapshotId, userId: s.userId }))
    );
  }

  // ── PSS User Search ────────────────────────────────────────────────────────

  searchPssUsers(query: string): Observable<{ userId: string; userName: string; allianceId: string }[]> {
    const s = this.currentSession;
    if (!s) return of([]);
    const token  = encodeURIComponent(s.accessToken);
    const server = s.productionServer;

    const parseUsers = (xml: string) => {
      const doc = new DOMParser().parseFromString(xml, 'text/xml');
      return Array.from(doc.querySelectorAll('User')).map(el => ({
        userId:     el.getAttribute('Id') || el.getAttribute('UserId') || '',
        userName:   el.getAttribute('Name') || el.getAttribute('UserName') || '',
        allianceId: el.getAttribute('AllianceId') || el.getAttribute('GuildId') || '',
      })).filter(u => !!u.userId && u.userId !== s.userId);
    };

    return this.http.get(
      `/pss/${server}/UserService/SearchUsers?searchString=${encodeURIComponent(query)}&skip=0&take=25&accessToken=${token}`,
      { responseType: 'text' }
    ).pipe(
      map(xml => parseUsers(xml)),
      catchError(() => of([]))
    );
  }

  searchPssFleets(query: string): Observable<{ allianceId: string; allianceName: string; memberCount?: string; trophy?: string }[]> {
    const s = this.currentSession;
    if (!s) return of([]);
    const token  = encodeURIComponent(s.accessToken);
    const server = s.productionServer;
    return this.http.get(
      `/pss/${server}/AllianceService/SearchAlliances?name=${encodeURIComponent(query)}&skip=0&take=25&accessToken=${token}`,
      { responseType: 'text' }
    ).pipe(
      map(xml => {
        const doc = new DOMParser().parseFromString(xml, 'text/xml');
        return Array.from(doc.querySelectorAll('Alliance')).map(el => ({
          allianceId:   el.getAttribute('AllianceId') || el.getAttribute('Id') || '',
          allianceName: el.getAttribute('AllianceName') || el.getAttribute('Name') || '',
          memberCount:  el.getAttribute('AllianceMemberCount') || el.getAttribute('MemberCount') || '',
          trophy:       el.getAttribute('Trophy') || '',
        })).filter(a => !!a.allianceId);
      }),
      catchError(() => of([]))
    );
  }

  // ── AI Library ─────────────────────────────────────────────────────────────

  getAiLibrary(allianceId = ''): Observable<{ folders: AiLibraryFolder[]; unfiled: AiLibrarySnapshot[]; shared: AiSharedLibrarySnapshot[] }> {
    const s = this.currentSession;
    if (!s) return of({ folders: [], unfiled: [], shared: [] });
    const params = `userId=${encodeURIComponent(s.userId)}&allianceId=${encodeURIComponent(allianceId)}`;
    return this.http.get<{ folders: AiLibraryFolder[]; unfiled: AiLibrarySnapshot[]; shared: AiSharedLibrarySnapshot[] }>(`/api/ai/library?${params}`).pipe(
      catchError(() => of({ folders: [], unfiled: [], shared: [] }))
    );
  }

  saveAiLibrary(name: string, description: string, rules: { conditionTypeId: string; actionTypeId: string }[], folderId?: string | null): Observable<AiLibrarySnapshot> {
    return this.ensureFreshSession().pipe(
      switchMap(s => this.http.post<AiLibrarySnapshot>('/api/ai/library', { userId: s.userId, name, description, rules, folderId: folderId ?? null }))
    );
  }

  deleteAiLibrary(libraryId: string): Observable<void> {
    return this.ensureFreshSession().pipe(
      switchMap(s => this.http.post<void>('/api/ai/library/delete', { libraryId, userId: s.userId }))
    );
  }

  moveAiLibraryEntry(libraryId: string, folderId: string | null): Observable<void> {
    return this.ensureFreshSession().pipe(
      switchMap(s => this.http.post<void>('/api/ai/library/move', { libraryId, folderId, userId: s.userId }))
    );
  }

  shareAiLibrary(libraryId: string, shareType: 'fleet' | 'player', targetId: string): Observable<AiLibraryShare> {
    return this.ensureFreshSession().pipe(
      switchMap(s => this.http.post<AiLibraryShare>('/api/ai/library/share', { libraryId, userId: s.userId, shareType, targetId }))
    );
  }

  unshareAiLibrary(shareId: string): Observable<void> {
    return this.ensureFreshSession().pipe(
      switchMap(s => this.http.post<void>('/api/ai/library/unshare', { shareId, userId: s.userId }))
    );
  }

  // ── AI Library Folders ─────────────────────────────────────────────────────

  saveAiFolder(name: string): Observable<AiLibraryFolder> {
    return this.ensureFreshSession().pipe(
      switchMap(s => this.http.post<AiLibraryFolder>('/api/ai/library/folder', { userId: s.userId, name }))
    );
  }

  deleteAiFolder(folderId: string): Observable<void> {
    return this.ensureFreshSession().pipe(
      switchMap(s => this.http.post<void>('/api/ai/library/folder/delete', { folderId, userId: s.userId }))
    );
  }

  shareAiFolder(folderId: string, shareType: 'fleet' | 'player', targetId: string): Observable<AiLibraryShare> {
    return this.ensureFreshSession().pipe(
      switchMap(s => this.http.post<AiLibraryShare>('/api/ai/library/folder/share', { folderId, userId: s.userId, shareType, targetId }))
    );
  }

  unshareAiFolder(shareId: string): Observable<void> {
    return this.ensureFreshSession().pipe(
      switchMap(s => this.http.post<void>('/api/ai/library/folder/unshare', { shareId, userId: s.userId }))
    );
  }

  persistBattles(battles: PssBattle[]): Observable<{ inserted: number; skipped: number }> {
    if (!battles.length) return of({ inserted: 0, skipped: 0 });
    return this.http.post<{ inserted: number; skipped: number }>('/api/battles', battles).pipe(
      catchError(() => of({ inserted: 0, skipped: 0 }))
    );
  }

  listRedeemCodes(status: 'active' | 'expired' | 'all' = 'active'): Observable<RedeemCodeRecord[]> {
    return this.http.get<{ codes: RedeemCodeRecord[] }>(`/api/redeem-codes?status=${status}`).pipe(
      map(r => r.codes ?? []),
      catchError(() => of([] as RedeemCodeRecord[]))
    );
  }

  addRedeemCode(code: string): Observable<RedeemCodeRecord[]> {
    return this.ensureFreshSession().pipe(
      switchMap(s => this.http.post<{ codes: RedeemCodeRecord[] }>('/api/redeem-codes', {
        code,
        createdByUserId: s.userId,
      })),
      map(r => r.codes ?? []),
      catchError(() => of([] as RedeemCodeRecord[]))
    );
  }

  redeemCode(code: string): Observable<RedeemCodeResult> {
    return this.ensureFreshSession().pipe(
      switchMap(s => {
        const server = s.productionServer || 'api.pixelstarships.com';
        return this.http.post<RedeemCodeResult>('/api/redeem-codes/use', {
          code,
          userId: s.userId,
          accessToken: s.accessToken,
          server,
        });
      }),
      catchError(err => {
        const message = err?.error?.error || err?.message || 'Redeem code failed';
        return throwError(() => new Error(message));
      })
    );
  }

  // ── Persist fleet snapshot to D1 (Cloudflare) ────────────────────────────
  // Fire-and-forget: upserts today's fleet member stats into tournament_daily_snapshots.
  // Safe to call multiple times per day — each call overwrites the row for today.
  persistFleetSnapshot(fleet: PssFleetInfo): Observable<{ upserted: number }> {
    if (!fleet.members.length) return of({ upserted: 0 });
    const body = { allianceId: fleet.allianceId, allianceName: fleet.allianceName, members: fleet.members };
    return this.http.post<{ upserted: number }>('/api/fleet-snapshots', body).pipe(
      catchError(() => of({ upserted: 0 }))
    );
  }

  getFleetSnapshots(allianceId: string, startDate?: string, endDate?: string): Observable<FleetDailySnapshot[]> {
    let url = `/api/fleet-snapshots?allianceId=${allianceId}`;
    if (startDate) url += `&startDate=${startDate}`;
    if (endDate)   url += `&endDate=${endDate}`;
    return this.http.get<{ snapshots: FleetDailySnapshot[] }>(url).pipe(
      map(r => r.snapshots ?? []),
      catchError(() => of([] as FleetDailySnapshot[]))
    );
  }

  // ── Tournament ────────────────────────────────────────────────────────────

  getTournamentSummary(allianceId: string, month: number, year: number): Observable<TournamentSummary | null> {
    return this.http.get<TournamentSummary>(`/api/tournament?allianceId=${allianceId}&month=${month}&year=${year}`).pipe(
      catchError(() => of(null))
    );
  }

  getTournamentBattles(userId: string, startDate: string, endDate: string): Observable<PssBattle[]> {
    return this.http.get<{ battles: PssBattle[] }>(
      `/api/tournament/battles?userId=${userId}&startDate=${startDate}&endDate=${endDate}`
    ).pipe(
      map(r => r.battles ?? []),
      catchError(() => of([] as PssBattle[]))
    );
  }

  getTournamentAvailableMonths(allianceId: string): Observable<{ year: number; month: number }[]> {
    return this.http.get<{ months: { year: number; month: number }[] }>(
      `/api/tournament/available-months?allianceId=${allianceId}`
    ).pipe(
      map(r => r.months ?? []),
      catchError(() => of([] as { year: number; month: number }[]))
    );
  }

  getTournamentMemberHistory(userId: string, take = 100): Observable<YaDcUserHistoryEntry[]> {
    return this.http.get<{ userId: string; history: YaDcUserHistoryEntry[] }>(
      `/api/tournament/member-history?userId=${encodeURIComponent(userId)}&take=${take}`
    ).pipe(
      map(r => r.history ?? []),
      catchError(() => of([] as YaDcUserHistoryEntry[]))
    );
  }

  getTournamentForecast(allianceId: string, month: number, year: number): Observable<TournamentForecast | null> {
    return this.http.get<TournamentForecast>(
      `/api/tournament/forecast?allianceId=${encodeURIComponent(allianceId)}&month=${month}&year=${year}`
    ).pipe(
      catchError(() => of(null))
    );
  }

  getTournamentForecastBacktest(allianceId: string, month: number, year: number): Observable<TournamentForecastBacktest | null> {
    return this.http.get<TournamentForecastBacktest>(
      `/api/tournament/forecast-backtest?allianceId=${encodeURIComponent(allianceId)}&month=${month}&year=${year}`
    ).pipe(
      catchError(() => of(null))
    );
  }

  private upsertAccount(session: PssSession): Observable<PssAccount> {
    const entry: PssAccount = {
      userId:        session.userId,
      name:          session.name,
      shipLevel:     session.shipLevel,
      deviceKey:     session.deviceKey,
      refreshToken:  session.refreshToken,
      lastLogin:     new Date().toISOString(),
    };
    try {
      const raw = localStorage.getItem('pss_accounts');
      const accounts: PssAccount[] = raw ? JSON.parse(raw) : [];
      const idx = accounts.findIndex(a => a.userId === entry.userId);
      if (idx >= 0) {
        if (!entry.refreshToken && accounts[idx].refreshToken) {
          entry.refreshToken = accounts[idx].refreshToken;
        }
        accounts[idx] = entry;
      } else {
        accounts.push(entry);
      }
      localStorage.setItem('pss_accounts', JSON.stringify(accounts));
    } catch {}

    return this.http.post<{ account: any }>('/api/accounts/login', {
      userId:       entry.userId,
      userName:     entry.name,
      fleetName:    entry.fleetName,
      shipLevel:    entry.shipLevel,
      pssServer:    session.productionServer || null,
    }).pipe(
      map(resp => {
        if (resp?.account?.isLocked) {
          throw new Error('Account is locked');
        }
        if (this.currentSession) {
          this.currentSession.isAdmin          = !!resp?.account?.isAdmin;
          this.currentSession.isServiceAccount = !!resp?.account?.isServiceAccount;
          this.currentSession.hasDeviceKey     = !!resp?.account?.hasDeviceKey;
          this.currentSession.hasRefreshToken  = !!resp?.account?.hasRefreshToken;
        }
        return entry;
      }),
      catchError(err => {
        // If the backend tracking call fails (e.g. local dev has no D1),
        // don't block the login — localStorage was already saved above.
        if (err?.status === 403 || err?.error?.error === 'Account is locked') {
          return throwError(() => new Error('Account is locked'));
        }
        return of(entry);
      })
    );
  }

  // ── Service-account credential sync ───────────────────────────────────────
  // Pushes the deviceKey + refreshToken stored in localStorage up to the
  // backend `accounts` row so the cron poller can DeviceLogin17 on this user's
  // behalf. Backend rejects callers whose account row isn't a service account.
  syncServiceCredentials(): Observable<{ hasDeviceKey: boolean; hasRefreshToken: boolean }> {
    const s = this.currentSession;
    if (!s) return throwError(() => new Error('Not logged in'));

    let deviceKey    = s.deviceKey    || '';
    let refreshToken = s.refreshToken || '';
    if (!deviceKey || !refreshToken) {
      try {
        const raw = localStorage.getItem('pss_accounts');
        const accounts: PssAccount[] = raw ? JSON.parse(raw) : [];
        const stored = accounts.find(a => a.userId === s.userId);
        if (stored) {
          if (!deviceKey)    deviceKey    = stored.deviceKey    || '';
          if (!refreshToken) refreshToken = stored.refreshToken || '';
        }
      } catch {}
    }
    if (!deviceKey || !refreshToken) {
      return throwError(() => new Error('No deviceKey/refreshToken found in this browser — log in fully on this device first'));
    }

    return this.http.post<{ account: any }>(
      '/api/accounts/me/credentials',
      { deviceKey, refreshToken },
      { headers: { 'X-User-Id': s.userId } }
    ).pipe(
      map(resp => {
        const acct = resp?.account ?? {};
        if (this.currentSession) {
          this.currentSession.hasDeviceKey    = !!acct.hasDeviceKey;
          this.currentSession.hasRefreshToken = !!acct.hasRefreshToken;
        }
        return {
          hasDeviceKey:    !!acct.hasDeviceKey,
          hasRefreshToken: !!acct.hasRefreshToken,
        };
      })
    );
  }
}

